import ErrorHandlerUtil, { ErrorCode, FWFunction } from "../utils/common/error-handler-util.ts";
import MixpanelTracker from "../utils/common/mixpanel-tracker.ts";
import { Utils } from "../utils/common/utils.js";
import { InputValidator } from '../utils/common/input-validator.ts';
import moment from "moment";
import { VariableGroupBy } from "@fundwave/types";

/**
 * @customfunction
 * @description Realm of the user's FW instance
 * @helpUrl https://fundwave.com/guide/reporting/Fundwave+Functions+for+Excel
 * @returns {string} Realm of the instance
 * @param {CustomFunctions.Invocation} invocation Invocation object.
 * @requiresAddress
 */
export async function realm(invocation) {
  let url = `https://app.getfundwave.com/services/api/instance/ssoconfig`;

  const baseUrl = localStorage.getItem("team");
  if (baseUrl) url = `${baseUrl}/services/api/instance/ssoconfig`;

  const ssoconfig = await fetch(url, {
    headers: {
      Accept: "application/json"
    }
  }).then((resp) => resp.json());

  MixpanelTracker.trackFunction({ functionName: FWFunction.realm, status: "success", cellAddress: invocation.address });

  return ssoconfig.realm;
}

/**
 * The IRR for the asset
 * @customfunction
 * @helpUrl https://fundwave.com/guide/reporting/IRR+Functions#f62430d4e81b4ea9a0ab65e065c45623
 * @param {number} [assetId] Asset ID
 * @param {number} [irrDate] IRR date (time will be omitted)
 * @param {number} [fundId] Fund ID. If omitted, value shown is across funds
 * @param {string} [valuation] Valuation. If omitted, valuation is retrieved automatically
 * @param {string} [displayCurrency] Display Currency, to display IRR in given currency.
 * @returns {string[][]} IRR for provided asset, in decimal format.
 * @param {CustomFunctions.Invocation} invocation Invocation object.
 * @requiresAddress
 */
export async function irra(assetId, irrDate, fundId, valuation, displayCurrency, invocation) {
  try {
    if (!assetId || !irrDate) throw ErrorCode.MissingRequiredProps;
    if (valuation != null && isNaN(Number(valuation))) return [["Valuation must be a number"]];

    await Utils.prepareOpenAPIClient();

    const { VariableAPI } = await import("../apiclient/variable.js");

    let context = {
      fromDate: "1900-01-01T00:00:00.000Z",
      toDate: Utils.getFormattedDate(irrDate),
      assets: { asset: [{ id: assetId }] }
    };

    if (fundId) context["fundId"] = fundId;
    else context["fundCurrency"] = await fetchFundCurrencyFromAssetId(assetId);

    if (displayCurrency) {
      if (!InputValidator.isValidCurrencyCode(displayCurrency)) {
        return [[InputValidator.INVALID_CURRENCY_CODE]];
      }
      context["outputCurrency"] = displayCurrency.toUpperCase();
    }

    if (valuation != null) context["numberList"] = { number: [valuation] };

    const result = await VariableAPI.getVariableValue("IRR", context); // [{value: 100.00}]
    const val = result?.[0]?.value || 0;

    MixpanelTracker.trackFunction({ functionName: FWFunction.irra, status: "success", cellAddress: invocation.address });

    return [[val]];
  } catch (err) {
    return ErrorHandlerUtil.errorHandler(err, FWFunction.irra, invocation.address, { assetId: !!assetId, irrDate: !!irrDate, valuation: valuation != null });
  }
}

/**
 * Calculates the IRR value
 * @customfunction
 * @helpUrl https://fundwave.com/guide/reporting/IRR+Functions#f6041421c58048c5a3b2088dcd0ac757
 * @param {number} [toDate] IRR dates (time will be omitted)
 * @param {string} [type] NET / GROSS IRR
 * @param {string[][]} [fundIds] Fund Ids, to calculate irr.
 * @param {string[][]} [assetIds] Asset Ids, to calculate net irr.
 * @param {string[][]} [investorCodes] Investor Codes, to calculate net irr.
 * @param {string} [valuation] Valuation. If omitted, valuation / NAV is retrieved automatically.
 * @param {string} [displayCurrency] Display Currency, to display IRR in given currency.
 * @returns {string[][]} IRR for provided assets or funds, in decimal format.
 * @param {CustomFunctions.Invocation} invocation Invocation object.
 * @requiresAddress
 */
export async function irr(toDate, type, fundIds, assetIds, investorCodes, valuation, displayCurrency, invocation) {
  try {
    await Utils.prepareOpenAPIClient();

    if (!assetIds && !fundIds && !investorCodes) throw ErrorCode.MissingOneOfProps;
    if (investorCodes && !fundIds) return [["Please enter fundIds along with investors"]];
    if (valuation != null && isNaN(Number(valuation))) return [["Valuation must be a number"]];

    const { VariableAPI } = await import("../apiclient/variable.js");

    if (fundIds) fundIds = Utils.convert2DArrayto1DArray(fundIds);
    if (assetIds) assetIds = Utils.convert2DArrayto1DArray(assetIds);
    if (investorCodes) investorCodes = Utils.convert2DArrayto1DArray(investorCodes);

    if (!toDate) toDate = Utils.getJSDateFromExcel(Utils.getExcelDateFromJS(new Date())).split("T")[0];
    else toDate = Utils.getFormattedDate(toDate).split("T")[0];

    if (type) {
      if (type.toLowerCase().includes("net")) type = "NET";
      if (type.toLowerCase().includes("gross")) type = "GROSS";
    }

    let groupBy, negate;
    let context = {
      toDate
    };

    if (displayCurrency) {
      if (!InputValidator.isValidCurrencyCode(displayCurrency)) {
        return [[InputValidator.INVALID_CURRENCY_CODE]];
      }
      context["outputCurrency"] = displayCurrency.toUpperCase();
    }

    if (type) context.idList = { id: [type] };

    if (valuation != null) context.numberList = { number: [valuation] };
    
    if (fundIds) {
      let fundIdArray = fundIds.map((fundId) => ({ fundId: parseInt(fundId) }));
      context.funds = {
        fund: fundIdArray
      };
    }

    if (assetIds) {
      let assetIdArray = assetIds.map((assetId) => ({ id: parseInt(assetId) }));
      context.assets = {
        asset: assetIdArray
      };
    }

    if (investorCodes) {
      let investorCodeArray = investorCodes.map((investorCode) => ({ code: investorCode }));
      context.investors = {
        investor: investorCodeArray
      };
    }

    const variableValues = await VariableAPI.getVariableValue("IRR", context, groupBy, negate);

    MixpanelTracker.trackFunction({ functionName: FWFunction.irr, status: "success", cellAddress: invocation.address });

    return Utils.formatArray(variableValues, "value", false, false, "irr");
  } catch (err) {
    return ErrorHandlerUtil.errorHandler(err, FWFunction.irr, invocation.address, {
      toDate: !!toDate,
      type: !!type,
      fundIds: Boolean(fundIds?.length),
      assetIds: Boolean(assetIds?.length),
      investorCodes: Boolean(investorCodes?.length),
      valuation: valuation != null
    });
  }
}

/**
 * List all assetaccounts of an asset
 * @customfunction
 * @helpUrl https://fundwave.com/guide/reporting/Asset+Account+Functions#05772927c3a640e6b2dd7bfb6662875c
 * @param {string[][]} [properties] Comma separated string of Field names
 * @param {number[][]} [assetIds] Asset Ids
 * @param {number[][]} [fundIds] Fund Ids
 * @param {number} [toDate] the asset accounts as of this date
 * @param {string} [statusFilter] Filter options: all | active | upcoming | realized | partiallyrealized
 * @param {number} [horizontal] If 1, horizontally displayed. If omitted, vertical display is used
 * @param {boolean} [showHeaders] Display headers
 * @returns {string[][]} A list of asset accounts
 * @param {CustomFunctions.Invocation} invocation Invocation object.
 * @requiresAddress
 */
export async function assetaccounts(properties, assetIds, fundIds, toDate, statusFilter, horizontal, showHeaders, invocation) {
  try {
    if (!Boolean(assetIds?.length) && !Boolean(fundIds?.length)) throw ErrorCode.MissingOneOfProps;

    const { AssetAccountAPIClient } = await Utils.prepareOpenAPIClient(Utils.APIClient.fundwave);

    if (Boolean(fundIds?.length)) {
      fundIds = fundIds.flat(1);
      statusFilter = statusFilter || "all";
    } else fundIds = [];

    assetIds = Boolean(assetIds?.length) ? assetIds.flat(1) : [];

    properties = Utils.handleProperties(properties);
    if (toDate) toDate = Utils.getFormattedDate(toDate).split("T")[0];
    
    const requestBody = { assetIds, fundIds, toDate };
    const assetAccounts = await AssetAccountAPIClient.getAssetAccounts(statusFilter || undefined, requestBody);

    if (!Boolean(assetAccounts?.length)) throw ErrorCode.DataNotFound;

    MixpanelTracker.trackFunction({ functionName: FWFunction.assetAccounts, status: "success", cellAddress: invocation.address });

    return Utils.formatArray(assetAccounts, properties, horizontal, showHeaders, "assetaccounts");
  } catch (err) {
    return ErrorHandlerUtil.errorHandler(err, FWFunction.assetAccounts, invocation.address);
  }
}

/**
 * Returns details of an assetaccount
 * @customfunction
 * @helpUrl https://fundwave.com/guide/reporting/Asset+Account+Functions#466d675ca1454f82af6b8498826ff778
 * @param {number} [assetAccountId] AssetAccount ID
 * @param {string[][]} [properties] Comma separated string of Field names
 * @param {number} [horizontal] If 1, horizontally displayed. If omitted, vertical display is used
 * @param {boolean} [showHeaders] Display headers
 * @returns {string[][]} Details of an assetAccount
 * @param {CustomFunctions.Invocation} invocation Invocation object.
 * @requiresAddress
 */
export async function assetaccount(assetAccountId, properties, horizontal, showHeaders, invocation) {
  try {
    if (!assetAccountId) throw ErrorCode.MissingRequiredProps;

    await Utils.prepareOpenAPIClient();

    const { AssetAPI } = await import("../apiclient/asset.js");

    const assetAccount = await AssetAPI.getAssetAccount(assetAccountId);
    if (!assetAccount) throw ErrorCode.DataNotFound;

    properties = Utils.handleProperties(properties);

    MixpanelTracker.trackFunction({ functionName: FWFunction.assetAccount, status: "success", cellAddress: invocation.address });

    return Utils.formatArray([assetAccount], properties, horizontal, showHeaders, "assetaccount");
  } catch (err) {
    return ErrorHandlerUtil.errorHandler(err, FWFunction.assetAccount, invocation.address);
  }
}

/**
 * List all accountCodes of a fund
 * @customfunction
 * @helpUrl https://fundwave.com/guide/reporting/Accounting+Functions#162191a99df64459b811e227f288bf42
 * @param {number} [fundId] Fund ID
 * @param {string[][]} [properties] Comma separated string of Field names
 * @param {number} [horizontal] If 1, horizontally displayed. If omitted, vertical display is used
 * @param {boolean} [showHeaders] Display headers
 * @returns {string[][]} A list of account codes
 * @param {CustomFunctions.Invocation} invocation Invocation object.
 * @requiresAddress
 */
export async function accountcodes(fundId, properties, horizontal, showHeaders, invocation) {
  try {
    if (!fundId) throw ErrorCode.MissingRequiredProps;

    const { AccountCodeAPIClient } = await Utils.prepareOpenAPIClient(Utils.APIClient.fundwave);
    const accountCodes = await AccountCodeAPIClient.getAccountCodes(fundId);
    if (!accountCodes) throw ErrorCode.DataNotFound;

    properties = Utils.handleProperties(properties);

    MixpanelTracker.trackFunction({ functionName: FWFunction.accountCodes, status: "success", cellAddress: invocation.address });

    return Utils.formatArray(accountCodes, properties, horizontal, showHeaders, "accountCodes");
  } catch (err) {
    return ErrorHandlerUtil.errorHandler(err, FWFunction.accountCodes, invocation.address);
  }
}

/**
 * List all variables of a fund
 * @customfunction
 * @helpUrl https://fundwave.com/guide/reporting/Variable+Functions#c9c96e6d0f6547c2a5674de193aa7db0
 * @param {number} [fundId] Fund ID
 * @param {string[][]} [properties] Comma separated string of Field names
 * @param {number} [horizontal] If 1, horizontally displayed. If omitted, vertical display is used
 * @param {boolean} [showHeaders] Display headers
 * @returns {string[][]} A list of all metrics
 * @param {CustomFunctions.Invocation} invocation Invocation object.
 * @requiresAddress
 */
export async function variables(fundId, properties, horizontal, showHeaders, invocation) {
  try {
    if (!fundId) throw ErrorCode.MissingRequiredProps;

    await Utils.prepareOpenAPIClient();

    const { VariableAPI } = await import("../apiclient/variable.js");
    const variables = await VariableAPI.getFundVariables(fundId);

    if (!Boolean(variables?.length)) throw ErrorCode.DataNotFound;

    properties = Utils.handleProperties(properties, "code");

    MixpanelTracker.trackFunction({ functionName: FWFunction.variables, status: "success", cellAddress: invocation.address });

    return Utils.formatArray(variables, properties, horizontal, showHeaders);
  } catch (err) {
    return ErrorHandlerUtil.errorHandler(err, FWFunction.variables, invocation.address);
  }
}

/**
 * Values for variable codes
 * @customfunction
 * @helpUrl https://fundwave.com/guide/reporting/Variable+Functions#23aa6c4b24cf4af384246becf0d72157
 * @param {string[][]} [variableCodes] Range of cells or Comma separated string of Variable Codes e.g. INVCOST, VALU, DRAW
 * @param {number} [toDate] the variable value as of this date
 * @param {number} [fromDate] the variable value as from date
 * @param {number[][]} [assetIds] Asset IDs
 * @param {string[][]} [investorCodes] Investor Codes
 * @param {number[][]} [fundIds] Fund IDs, use when fundCurrency is omitted. If omitted, value shown is across funds
 * @param {number[][]} [assetAccountIds] Asset Account IDs.
 * @param {string} [displayCurrency] Display Currency, to display values in given currency.
 * @param {string} [groupBy] GroupBy will show values aggregated for this field
 * @param {string} [properties] Comma separated string of Field names
 * @param {boolean} [horizontal] If 1, horizontally displayed. If omitted, vertical display is used.
 * @returns {string[][]} A list of value for the input variable codes.
 * @param {CustomFunctions.Invocation} invocation Invocation object.
 * @requiresAddress
 */
export async function variablevalue(
  variableCodes,
  toDate,
  fromDate,
  assetIds,
  investorCodes,
  fundIds,
  assetAccountIds,
  displayCurrency,
  groupBy,
  properties,
  horizontal,
  invocation
) {
  return getVariableValues(variableCodes, toDate, fromDate, assetIds, investorCodes, fundIds, assetAccountIds, displayCurrency, groupBy, false, properties, horizontal, invocation, FWFunction.variableValue);
}

/**
 * Investor values for variable codes
 * @customfunction
 * @helpUrl https://fundwave.com/guide/reporting/Variable+Functions#23aa6c4b24cf4af384246becf0d72157
 * @param {string[][]} [variableCodes] Range of cells or Comma separated string of Variable Codes e.g. INVCOST, VALU, DRAW
 * @param {string[][]} [investorCodes] Investor Codes
 * @param {number[][]} [fundIds] Fund IDs, use when fundCurrency is omitted. If omitted, value shown is across funds
 * @param {number[][]} [assetIds] Asset IDs
 * @param {number[][]} [assetAccountIds] Asset Account IDs.
 * @param {number} [toDate] the variable value as of this date
 * @param {number} [fromDate] the variable value as from date
 * @param {string} [groupBy] GroupBy will show values aggregated for this field
 * @param {boolean} [ignoreTransfers] Ignore transfer transactions between investors. Typically used for tax reporting to ensure gains are still attributable to transferor for tax reporting
 * @param {string} [displayCurrency] Display Currency, to display values in given currency.
 * @param {string} [properties] Comma separated string of Field names
 * @param {boolean} [horizontal] If 1, horizontally displayed. If omitted, vertical display is used.
 * @returns {string[][]} A list of value for the input variable codes.
 * @param {CustomFunctions.Invocation} invocation Invocation object.
 * @requiresAddress
 */
export async function investorvalue(
  variableCodes,
  investorCodes,
  fundIds,
  assetIds,
  assetAccountIds,
  toDate,
  fromDate,
  groupBy,
  ignoreTransfers,
  displayCurrency,
  properties,
  horizontal,
  invocation
) {
  try {
    if (!variableCodes || !investorCodes?.length || !fundIds?.length) throw ErrorCode.MissingRequiredProps;

    return getVariableValues(variableCodes, toDate, fromDate, assetIds, investorCodes, fundIds, assetAccountIds, displayCurrency, groupBy, ignoreTransfers, properties, horizontal, invocation, FWFunction.investorValue);
  } catch (err) {
    return ErrorHandlerUtil.errorHandler(err, FWFunction.investorValue, invocation.address, {
      variableCodes: !!variableCodes,
      investorCodes: !!investorCodes?.length,
      fundIds: !!fundIds?.length
    });
  }
}

/**
 * Aggregated value for variable codes
 * @customfunction
 * @helpUrl https://fundwave.com/guide/reporting/Variable+Functions#23aa6c4b24cf4af384246becf0d72157
 * @param {string[][]} variableCodes Range of cells or Comma separated string of Variable Codes e.g. INVCOST, VALU, DRAW
 * @param {number} toDate the variable value as of this date
 * @param {number} [fromDate] the variable value as from date
 * @param {number[][]} [assetIds] Asset IDs
 * @param {string[][]} [investorCodes] Investor Codes
 * @param {number[][]} [fundIds] Fund IDs, use when fundCurrency is omitted. If omitted, value shown is across funds
 * @param {number[][]} [assetAccountIds] Asset Account IDs.
 * @param {string} [fundCurrency] Fund Currency, use when fundId is omitted eg: USD for all US Dollars denominated funds
 * @param {string} [properties] Comma separated string of Field names
 * @param {boolean} [horizontal] If 1, horizontally displayed. If omitted, vertical display is used.
 * @returns {string[][]} A list of aggregated value for the input variable codes.
 */
/*async function variablevalueaggregate(variableCodes, toDate, fromDate, assetIds, investorCodes, fundIds, assetAccountIds, fundCurrency, properties, horizontal) {

  if (!assetIds && !investorCodes && !fundIds && !assetAccountIds)
    return [["Please enter atleast one of assetIds, investorCodes, fundIds, assetAccountIds"]];

  if ((investorCodes || assetAccountIds) && !fundIds)
    return [["Please enter fundId"]];


  if (assetIds?.length > 0)  assetIds = Utils.convert2DArrayto1DArray(assetIds);
  else  assetIds = [];

  if (investorCodes?.length > 0)  investorCodes = Utils.convert2DArrayto1DArray(investorCodes);
  else investorCodes = [];

  if (fundIds?.length > 0)  fundIds = Utils.convert2DArrayto1DArray(fundIds);
  else  fundIds = [];

  if (assetAccountIds?.length > 0)  assetAccountIds = Utils.convert2DArrayto1DArray(assetAccountIds);
  else  assetAccountIds = [];

  if (!fundCurrency && !fundIds)
    fundCurrency = await fetchFundCurrencyFromAssetId(assetIds[0]);

  let VariableAPI = await import('../apiclient/variable.js'); VariableAPI = VariableAPI.VariableAPI;

  try {

    let context = {
      "fromDate": fromDate ? Utils.getFormattedDate(fromDate) : "1900-01-01T00:00:00+0000",
      "toDate": Utils.getFormattedDate(toDate)
    };

    if (investorCodes?.length > 0) {
      let investorCodeArray = investorCodes.map(investorCode => {return {code:investorCode}});
      context["investors"] = {investor:investorCodeArray};
    }
    if (assetIds?.length > 0) {
      let assetIdArray = assetIds.map(assetId => {return {id:assetId}});
      context["assets"] = {asset:assetIdArray};
    }
    if (assetAccountIds?.length > 0) {
      let assetAccountIdArray = assetAccountIds.map(assetAccountId => {return {id:assetAccountId}});
      context["assetAccounts"] = {assetAccount:assetAccountIdArray};
    }

    if (fundIds?.length > 0) {
      let fundIdArray = fundIds.map(fundId => {return {fundId:fundId}});
      context["funds"] = {fund:fundIdArray};
    }
    else if (!investorCodes) context["fundCurrency"] = fundCurrency.toUpperCase();

    let variableValues = await VariableAPI.getVariableValue(variableCodes, context);

    if (!properties) properties = "value";
    if (!properties.split(",").includes("value")) properties += ",value";

    let result = Utils.formatArray(variableValues, properties, horizontal);
;
  }
  catch (err) {
    console.log(err);
    return [[err]];
  }
}*/

/**
 * Get details of an asset
 * @customfunction
 * @helpUrl https://fundwave.com/guide/reporting/Asset+Functions#a98b440ed1e442bab901399ac9c407ac
 * @param {string[][]} [properties] Comma separated string of Field names
 * @param {number} [assetId] Asset ID
 * @param {number} [fundId] Fund ID, to fetch asset fund details
 * @param {number} [toDate] The custom field values as of this date
 * @param {number} [horizontal] If 1, horizontally displayed. If omitted, vertical display is used
 * @param {boolean} [showHeaders] Display headers
 * @returns {string[][]} Details of an asset
 * @param {CustomFunctions.Invocation} invocation Invocation object.
 * @requiresAddress
 */
export async function asset(properties, assetId, fundId, toDate, horizontal, showHeaders, invocation) {
  try {
    if (!assetId) throw ErrorCode.MissingRequiredProps;

    const { AssetAPIClient } = await Utils.prepareOpenAPIClient(Utils.APIClient.fundwave);

    const propertyArray = Array.isArray(properties) ? properties.flat(1).join(",").split(",").map(prop => prop.trim().toLowerCase().replaceAll(" ", "")) : [];
    const toFetchCustomFields = properties && propertyArray.some((property) => !Utils.assetBasicFields.includes(property) && !Utils.assetFundFields.includes(property));

    let fetchedAssets = !!fundId ? await AssetAPIClient.getAssets([assetId], [fundId]) : await AssetAPIClient.getAssets([assetId]);
    if (!Boolean(fetchedAssets?.length)) throw ErrorCode.DataNotFound;

    fetchedAssets = fetchedAssets.map((asset) => (asset.financialYearEnd ? { ...asset, financialYearEnd: Utils.getExcelDateFromJS(asset.financialYearEnd, true) } : asset));

    if (fundId) {
      fetchedAssets = fetchedAssets.map((asset) => {
        asset.assetFunds.forEach((assetFund) => {
          if (assetFund.fund.id.toString() === fundId.toString()) {
            asset = { ...asset, ...assetFund };
          }
        });
        return asset;
      });
    }

    if (toFetchCustomFields) fetchedAssets = await Utils.prepareCustomFieldValues({ type: "ASSET" }, fetchedAssets, propertyArray, toDate);

    properties = Utils.handleProperties(properties);

    MixpanelTracker.trackFunction({ functionName: FWFunction.asset, status: "success", cellAddress: invocation.address });

    return Utils.formatArray(fetchedAssets, properties, horizontal, showHeaders, "assets");
  } catch (err) {
    return ErrorHandlerUtil.errorHandler(err, FWFunction.asset, invocation.address);
  }
}

/**
 * Get asset update types
 * @customfunction
 * @helpUrl https://fundwave.com/guide/reporting/Text+Updates+Functions#bd98d08e8489422d9f7eb7a7d2987b02
 * @param {string[][]} [properties] Comma separated string of Field names
 * @param {number} [horizontal] If 1, horizontally displayed. If omitted, vertical display is used
 * @param {boolean} [showHeaders] Display headers
 * @returns {string[][]} A list of asset update types
 * @param {CustomFunctions.Invocation} invocation Invocation object.
 * @requiresAddress
 */
export async function assetupdatetypes(properties, horizontal, showHeaders, invocation) {
  try {
    await Utils.prepareOpenAPIClient();

    const { AssetAPI } = await import("../apiclient/asset.js");

    const assetUpdateTypes = await AssetAPI.getAssetUpdateTypes();

    if (!Boolean(assetUpdateTypes?.length)) throw ErrorCode.DataNotFound;

    properties = Utils.handleProperties(properties, "type");

    MixpanelTracker.trackFunction({ functionName: FWFunction.assetUpdateTypes, status: "success", cellAddress: invocation.address });

    return Utils.formatArray(assetUpdateTypes, properties, horizontal, showHeaders);
  } catch (err) {
    return ErrorHandlerUtil.errorHandler(err, FWFunction.assetUpdateTypes, invocation.address);
  }
}

/**
 * Get submission update types
 * @customfunction
 * @helpUrl https://fundwave.com/guide/reporting/Text+Updates+Functions#b909de437b804d0dac0e97efc895b93a
 * @param {string[][]} [properties] Comma separated string of Field names
 * @param {number} [horizontal] If 1, horizontally displayed. If omitted, vertical display is used
 * @param {boolean} [showHeaders] Display headers
 * @returns {string[][]} A list of submission update types
 * @param {CustomFunctions.Invocation} invocation Invocation object.
 * @requiresAddress
 */
export async function submissionupdatetypes(properties, horizontal, showHeaders, invocation) {
  try {
    await Utils.prepareOpenAPIClient();

    const { AssetAPI } = await import("../apiclient/asset.js");

    const submissionUpdateTypes = await AssetAPI.getSubmissionUpdateTypes();
    if (!Boolean(submissionUpdateTypes?.length)) throw ErrorCode.DataNotFound;

    properties = Utils.handleProperties(properties, "type");

    MixpanelTracker.trackFunction({ functionName: FWFunction.submissionUpdateTypes, status: "success", cellAddress: invocation.address });

    return Utils.formatArray(submissionUpdateTypes, properties, horizontal, showHeaders);
  } catch (err) {
    return ErrorHandlerUtil.errorHandler(err, FWFunction.submissionUpdateTypes, invocation.address);
  }
}

/**
 * Get asset updates of an asset
 * @customfunction
 * @helpUrl https://fundwave.com/guide/reporting/Text+Updates+Functions#05fc467600d446c1a18529e9e0d3082d
 * @param {number} [assetId] Asset ID
 * @param {number} [toDate] asset updates as of this date
 * @param {number} [fromDate] asset updates as from date
 * @param {string} [category] category of asset updates
 * @param {string[][]} [properties] Comma separated string of Field names
 * @param {number} [horizontal] If 1, horizontally displayed. If omitted, vertical display is used
 * @param {boolean} [showHeaders] Display headers
 * @returns {string[][]} A list of asset updates
 * @param {CustomFunctions.Invocation} invocation Invocation object.
 * @requiresAddress
 */
export async function assetupdates(assetId, toDate, fromDate, category, properties, horizontal, showHeaders, invocation) {
  try {
    await Utils.prepareOpenAPIClient();

    if (!assetId || !toDate) throw ErrorCode.MissingRequiredProps;

    const { AssetAPI } = await import("../apiclient/asset.js");
    const earliestDate = "1900-01-01";

    toDate = Utils.getFormattedDate(toDate).split("T")[0];

    fromDate = !fromDate ? earliestDate : Utils.getFormattedDate(fromDate).split("T")[0];

    let categoryId = category;
    if (category && isNaN(+category)) {
      categoryId = await Utils.getIdFromCategoryName(category, "asset");
    }

    let assetUpdates = await AssetAPI.getAssetUpdates(assetId, toDate, fromDate, categoryId);
    if (!Boolean(assetUpdates?.length)) throw ErrorCode.DataNotFound;

    properties = Utils.handleProperties(properties, "text");

    if (fromDate === earliestDate) assetUpdates = [assetUpdates[0]]; //display latest update, if only toDate is provided

    MixpanelTracker.trackFunction({ functionName: FWFunction.assetUpdates, status: "success", cellAddress: invocation.address });

    return Utils.formatArray(assetUpdates, properties, horizontal, showHeaders);
  } catch (err) {
    return ErrorHandlerUtil.errorHandler(err, FWFunction.assetUpdates, invocation.address, { assetId: !!assetId, toDate: !!toDate });
  }
}

/**
 * Get submission updates of an asset
 * @customfunction
 * @helpUrl https://fundwave.com/guide/reporting/Text+Updates+Functions#124dacdc4ea94233972b9cb0ad6d5746
 * @param {number} [assetId] Asset ID
 * @param {number} [toDate] asset submission updates as of this date
 * @param {number} [fromDate] asset submission updates as from date
 * @param {string} [category] category of submission updates
 * @param {string[][]} [properties] Comma separated string of Field names
 * @param {number} [horizontal] If 1, horizontally displayed. If omitted, vertical display is used
 * @param {boolean} [showHeaders] Display headers
 * @returns {string[][]} A list of submission updates of an asset
 * @param {CustomFunctions.Invocation} invocation Invocation object.
 * @requiresAddress
 */
export async function submissionupdates(assetId, toDate, fromDate, category, properties, horizontal, showHeaders, invocation) {
  try {
    await Utils.prepareOpenAPIClient();

    if (!assetId || !toDate) throw ErrorCode.MissingRequiredProps;

    const { AssetAPI } = await import("../apiclient/asset.js");
    const earliestDate = "1900-01-01";

    toDate = Utils.getFormattedDate(toDate).split("T")[0];

    fromDate = !fromDate ? earliestDate : Utils.getFormattedDate(fromDate).split("T")[0];

    let categoryId = category;
    if (category && isNaN(+category)) {
      categoryId = await Utils.getIdFromCategoryName(category, "submission");
    }

    let submissionUpdates = await AssetAPI.getSubmissionUpdates(assetId, toDate, fromDate, categoryId);
    if (!Boolean(submissionUpdates?.length)) throw ErrorCode.DataNotFound;

    properties = Utils.handleProperties(properties, "text");

    if (fromDate === earliestDate) submissionUpdates = [submissionUpdates[0]]; //display latest update, if only toDate is provided

    MixpanelTracker.trackFunction({ functionName: FWFunction.submissionUpdates, status: "success", cellAddress: invocation.address });

    return Utils.formatArray(submissionUpdates, properties, horizontal, showHeaders);
  } catch (err) {
    return ErrorHandlerUtil.errorHandler(err, FWFunction.submissionUpdates, invocation.address, { assetId: !!assetId, toDate: !!toDate });
  }
}

/**
 * Returns all assets the logged in user has access to.
 * @customfunction
 * @helpUrl https://fundwave.com/guide/reporting/Asset+Functions#0072dc3ef2174960ab330f2df38b1072
 * @param {string[][]} [properties] Comma separated string of Field names
 * @param {number} [fundId] Fund ID, to fetch assets of a particular fund
 * @param {string} [statusFilter] Filter options: all | active | upcoming | realized | partiallyrealized
 * @param {number} [toDate] List of assets and related custom field value as of this date
 * @param {number} [horizontal] If 1, horizontally displayed. If omitted, vertical display is used.
 * @param {boolean} [showHeaders] Display headers
 * @returns {string[][]} A list of assets.
 * @param {CustomFunctions.Invocation} invocation Invocation object.
 * @requiresAddress
 */
export async function assets(properties, fundId, statusFilter, toDate, horizontal, showHeaders, invocation) {
  try {
    const { AssetAPIClient } = await Utils.prepareOpenAPIClient(Utils.APIClient.fundwave);

    const propertyArray = Array.isArray(properties) ? properties.flat(1).join(",").split(",").map(prop => prop.trim().toLowerCase().replaceAll(" ", "")) : [];
    const toFetchCustomFields = properties && propertyArray.some((property) => !Utils.assetBasicFields.includes(property.toLowerCase()) && !Utils.assetFundFields.includes(property.toLowerCase()));
    const toFetchAssetFundDetails = fundId && properties && propertyArray.some((property) => Utils.assetFundFields.includes(property.toLowerCase()));
    
    let fetchedAssets = await AssetAPIClient.getAssetsWithFilter(
      statusFilter || undefined,
      { ...(fundId ? { fundIds: [fundId] } : {}), ...(toDate ? { toDate: Utils.getFormattedDate(toDate).split("T")[0] } : {}) }
    );
    
    if (!Boolean(fetchedAssets?.length)) throw ErrorCode.DataNotFound;

    fetchedAssets = fetchedAssets.map((asset) => (asset.financialYearEnd ? { ...asset, financialYearEnd: Utils.getExcelDateFromJS(asset.financialYearEnd, true) } : asset));

    if (toFetchAssetFundDetails) {
      const assetsWithFundDetails = await AssetAPIClient.getAssets(fetchedAssets.map((asset) => asset.id), [fundId], ["id", "assetFunds"]);
      const assetsById = assetsWithFundDetails.reduce((acc, asset) => {
        acc[asset.id] = asset;
        return acc;
      }, {});
      fetchedAssets = fetchedAssets?.map((eachAsset) => {
        assetsById[eachAsset.id]?.assetFunds?.forEach((assetFund) => {
          if (assetFund.fund.id.toString() === fundId.toString()) {
            eachAsset = { ...eachAsset, ...assetFund };
          }
        });
        return eachAsset;
      });
    }

    if (toFetchCustomFields) fetchedAssets = await Utils.prepareCustomFieldValues({ type: "ASSET" }, fetchedAssets, propertyArray, toDate);

    properties = Utils.handleProperties(properties);

    MixpanelTracker.trackFunction({ functionName: FWFunction.assets, status: "success", cellAddress: invocation.address });
    fetchedAssets = fetchedAssets.map((asset) => {
      return { ...asset, captableId: asset.id };
    });
    return Utils.formatArray(fetchedAssets, properties, horizontal, showHeaders, "assets");
  } catch (err) {
    return ErrorHandlerUtil.errorHandler(err, FWFunction.assets, invocation.address);
  }
}


/**
 * Returns all underlying assets the logged in user has access to.
 * @customfunction
 * @param {string[][]} [properties] Comma separated string of Field names
 * @param {number} [fundId] Fund of Fund ID, to fetch underlying assets of a particular fund
 * @param {number[][]} [parentAssetIds] Parent Asset IDs, to fetch underlying assets of particular parent assets
 * @param {number} [toDate] The custom field value as of this date
 * @param {number} [horizontal] If 1, horizontally displayed. If omitted, vertical display is used.
 * @param {boolean} [showHeaders] Display headers
 * @returns {string[][]} A list of assets.
 * @param {CustomFunctions.Invocation} invocation Invocation object.
 * @requiresAddress
 */
export async function underlyingAssets(properties, fundId, parentAssetIds, toDate, horizontal, showHeaders, invocation) {
  try {
    if (!fundId && !parentAssetIds?.length) throw ErrorCode.MissingOneOfProps;

    const { AssetAPIClient } = await Utils.prepareOpenAPIClient(Utils.APIClient.fundwave);
    const propertyArray = Array.isArray(properties) ? properties.flat(1).join(",").split(",").map(prop => prop.trim().toLowerCase().replaceAll(" ", "")) : [];
    const toFetchCustomFields = properties && propertyArray.some((property) => !Utils.assetBasicFields.includes(property.toLowerCase()) && !Utils.assetFundFields.includes(property.toLowerCase()));
    parentAssetIds = Boolean(parentAssetIds?.length) ? Utils.convert2DArrayto1DArray(parentAssetIds) : [];
    const fundIds = fundId ? [fundId] : undefined;
    let assets = await AssetAPIClient.getUnderlyingAssets(parentAssetIds, fundIds);

    if (!Boolean(assets?.length)) throw ErrorCode.DataNotFound;

    assets = assets.map((asset) => (asset.financialYearEnd ? { ...asset, financialYearEnd: Utils.getExcelDateFromJS(asset.financialYearEnd, true) } : asset));

    if (toFetchCustomFields) assets = await Utils.prepareCustomFieldValues({ type: "ASSET" }, assets, propertyArray, toDate);

    properties = Utils.handleProperties(properties);

    MixpanelTracker.trackFunction({ functionName: FWFunction.underlyingAssets, status: "success", cellAddress: invocation.address });
    assets = assets.map((asset) => {
      return { ...asset, captableId: asset.id };
    });
    return Utils.formatArray(assets, properties, horizontal, showHeaders, "underlyingassets");
  } catch (err) {
    return ErrorHandlerUtil.errorHandler(err, FWFunction.underlyingAssets, invocation.address);
  }
}

/**
 * List all metrics of an asset
 * @customfunction
 * @helpUrl https://fundwave.com/guide/reporting/Metric+Functions#0041ea9087b14ddd95804fa3a1470acc
 * @param {number} [assetId] Asset ID
 * @param {string[][]} [properties] Comma separated string of Field names
 * @param {number} [horizontal] If 1, horizontally displayed. If omitted, vertical display is used
 * @param {boolean} [showHeaders] Display headers
 * @returns {string[][]} A list of all metrics
 * @param {CustomFunctions.Invocation} invocation Invocation object.
 * @requiresAddress
 */
export async function metrics(assetId, properties, horizontal, showHeaders, invocation) {
  try {
    await Utils.prepareOpenAPIClient();

    if (!assetId) throw ErrorCode.MissingRequiredProps;

    const { MetricAPI } = await import("../apiclient/metric.js");

    const metrics = await MetricAPI.getAssetMetrics(assetId);
    if (!Boolean(metrics?.length)) throw ErrorCode.DataNotFound;

    properties = Utils.handleProperties(properties);

    MixpanelTracker.trackFunction({ functionName: FWFunction.metrics, status: "success", cellAddress: invocation.address });

    return Utils.formatArray(metrics, properties, horizontal, showHeaders);
  } catch (err) {
    return ErrorHandlerUtil.errorHandler(err, FWFunction.metrics, invocation.address);
  }
}

/**
 * Get values of asset metrics
 * @customfunction
 * @helpUrl https://fundwave.com/guide/reporting/Metric+Functions#694d40ae0eb14c639f791ffda9403585
 * @param {string[][]} [metricCodes] Range of cells or Comma separated string of Metric Codes eg. NETREV, CASH, LTV
 * @param {number} [toDate] the variable value as of this date
 * @param {string} [periodType] eg. LM, LQ, LTM, YTD, SI. Default value -> LQ
 * @param {number[][]} [assetIds] Asset IDs
 * @param {string} [currency] Currency code, to display values in given currency.
 * @param {number} [scenarioId] Scenario ID eg. 1 for Actual, 2 for Forecast
 * @param {string[][]} [properties] Comma separated string of Field names
 * @param {number} [horizontal] If 1, horizontally displayed. If omitted, vertical display is used
 * @param {string} [category] Category name, use fw.metriccategories to get list of available categories
 * @returns {string[][]} Values of asset metrics
 * @param {CustomFunctions.Invocation} invocation Invocation object.
 * @requiresAddress
 */
export async function metricvalue(metricCodes, toDate, periodType, assetIds, currency, scenarioId, properties, horizontal, category, invocation) {
  try {
    if (!Boolean(assetIds?.length) || !Boolean(metricCodes?.length) || !toDate) throw ErrorCode.MissingRequiredProps;

    if (!periodType) periodType = "LQ";
    if (!scenarioId) scenarioId = 1;

    const { MetricAPIClient } = await Utils.prepareOpenAPIClient(Utils.APIClient.fundwave);
    const { MetricAPI } = await import("../apiclient/metric.js");

    metricCodes = Utils.convert2DArrayto1DArray(metricCodes);
    assetIds = Utils.convert2DArrayto1DArray(assetIds);
    toDate = Utils.getFormattedDate(toDate).split("T")[0];
    periodType = periodType.toUpperCase();

    let verticalIds = [];
    if (!!category) {
      const verticals = await MetricAPI.getMetricVerticals();
      const vertical = verticals.find((vertical) => vertical.name.toLowerCase() === category.toLowerCase());
      if (vertical) verticalIds = [vertical.id];
    }

    const context = {
      assetIds,
      codes: metricCodes,
      toDate,
      periodTypeId: periodType,
      scenarioId,
      verticalIds: !!verticalIds.length ? verticalIds : null
    };

    if (currency) context.currencyCode = currency.toUpperCase();

    const metricValues = await MetricAPIClient.getMetricValues(context);

    if (!Boolean(metricValues?.length)) throw ErrorCode.DataNotFound;

    //make metricValues a metricCodes * assets matrix
    const metricValuesMatrix = getMatrixFromMetricValues(metricValues, metricCodes, assetIds);

    properties = Utils.handleProperties(properties, "value");
    if (!properties.split(",").includes("value")) properties += ",value";

    let result = Utils.formatArray(metricValuesMatrix, properties, false, false, "metricValues");
    if (horizontal) result = Utils.transposeOfMatrix(result);

    MixpanelTracker.trackFunction({ functionName: FWFunction.metricValue, status: "success", cellAddress: invocation.address });

    return result;
  } catch (err) {
    return ErrorHandlerUtil.errorHandler(err, FWFunction.metricValue, invocation.address, { assetIds: !!assetIds, metricCodes: !!metricCodes, toDate: !!toDate });
  }
}

/**
 * Get details of a fund
 * @customfunction
 * @helpUrl https://fundwave.com/guide/reporting/Fund+Functions#3d423a32271340238552b9bb2b8d9d00
 * @param {number} [fundId] Fund ID
 * @param {number} [assetId] Asset ID
 * @param {string[][]} [properties] Comma separated string of Field names
 * @param {number} [horizontal] If 1, horizontally displayed. If omitted, vertical display is used
 * @param {boolean} [showHeaders] Display headers
 * @returns {string[][]} Details of the fund
 * @param {CustomFunctions.Invocation} invocation Invocation object.
 * @requiresAddress
 */
export async function fund(fundId, assetId, properties, horizontal, showHeaders, invocation) {
  try {
    await Utils.prepareOpenAPIClient();

    if (!fundId) throw ErrorCode.MissingRequiredProps;

    const { FundAPI } = await import("../apiclient/fund.js");

    let toFetchAssetFunds = false;
    if (properties && assetId) {
      let assetFundFields = Utils.assetFundFields;

      let propertyArray = properties.flat(1);
      propertyArray = propertyArray.map((property) => property.split(",")).flat(1);
      propertyArray = propertyArray.map((property) => property.toLowerCase());

      for (let property of propertyArray) {
        if (assetFundFields.includes(property)) {
          toFetchAssetFunds = true;
          break;
        }
      }
    }

    const fund = await FundAPI.getFund(fundId, toFetchAssetFunds, assetId);
    if (!fund) throw ErrorCode.DataNotFound;

    MixpanelTracker.trackFunction({ functionName: FWFunction.fund, status: "success", cellAddress: invocation.address });
    properties = Utils.handleProperties(properties);
    return Utils.formatArray([fund], properties, horizontal, showHeaders);
  } catch (err) {
    return ErrorHandlerUtil.errorHandler(err, FWFunction.fund, invocation.address);
  }
}

/**
 * Get fund update types
 * @customfunction
 * @helpUrl https://fundwave.com/guide/reporting/Text+Updates+Functions#3b6bc3220bed40b4a2d571f7d4b1a3b6
 * @param {string[][]} [properties] Comma separated string of Field names
 * @param {number} [horizontal] If 1, horizontally displayed. If omitted, vertical display is used
 * @param {boolean} [showHeaders] Display headers
 * @returns {string[][]} A list of fund update types
 * @param {CustomFunctions.Invocation} invocation Invocation object.
 * @requiresAddress
 */
export async function fundupdatetypes(properties, horizontal, showHeaders, invocation) {
  try {
    await Utils.prepareOpenAPIClient();

    const { FundAPI } = await import("../apiclient/fund.js");

    const fundUpdateTypes = await FundAPI.getFundUpdateTypes();
    if (!Boolean(fundUpdateTypes?.length)) throw ErrorCode.DataNotFound;

    properties = Utils.handleProperties(properties, "type");

    MixpanelTracker.trackFunction({ functionName: FWFunction.fundUpdateTypes, status: "success", cellAddress: invocation.address });

    return Utils.formatArray(fundUpdateTypes, properties, horizontal, showHeaders);
  } catch (err) {
    return ErrorHandlerUtil.errorHandler(err, FWFunction.fundUpdateTypes, invocation.address);
  }
}

/**
 * Get fund updates
 * @customfunction
 * @helpUrl https://fundwave.com/guide/reporting/Text+Updates+Functions#fcaf8c5640d04da5a3e04152a54f23c0
 * @param {number} [fundId] Fund ID
 * @param {number} [toDate] fund updates as of this date
 * @param {number} [fromDate] fund updates as from date
 * @param {string} [category] category of fund updates
 * @param {string[][]} [properties] Comma separated string of Field names
 * @param {number} [horizontal] If 1, horizontally displayed. If omitted, vertical display is used
 * @param {boolean} [showHeaders] Display headers
 * @returns {string[][]} A list of fund updates
 * @param {CustomFunctions.Invocation} invocation Invocation object.
 * @requiresAddress
 */
export async function fundupdates(fundId, toDate, fromDate, category, properties, horizontal, showHeaders, invocation) {
  try {
    await Utils.prepareOpenAPIClient();

    if (!fundId || !toDate) throw ErrorCode.MissingRequiredProps;

    const { FundAPI } = await import("../apiclient/fund.js");
    const earliestDate = "1900-01-01";

    toDate = Utils.getFormattedDate(toDate).split("T")[0];
    fromDate = !fromDate ? earliestDate : Utils.getFormattedDate(fromDate).split("T")[0];
    let categoryId = category;

    if (category && isNaN(+category)) {
      categoryId = await Utils.getIdFromCategoryName(category, "fund");
    }

    let fundUpdates = await FundAPI.getFundUpdates(fundId, toDate, fromDate, categoryId);
    if (!Boolean(fundUpdates?.length)) throw ErrorCode.DataNotFound;

    properties = Utils.handleProperties(properties, "text");

    if (fromDate === earliestDate) fundUpdates = [fundUpdates[0]]; //display latest update, if only toDate is provided

    MixpanelTracker.trackFunction({ functionName: FWFunction.fundUpdates, status: "success", cellAddress: invocation.address });

    return Utils.formatArray(fundUpdates, properties, horizontal, showHeaders);
  } catch (err) {
    return ErrorHandlerUtil.errorHandler(err, FWFunction.fundUpdates, invocation.address, { fundId: !!fundId, toDate: !!toDate });
  }
}

/**
 * List all funds
 * @customfunction
 * @helpUrl https://fundwave.com/guide/reporting/Fund+Functions#e9bd1c22f264439da1aa25dd8b559bdf
 * @param {number} [assetId] Asset ID, to fetch funds of an asset
 * @param {string[][]} [properties] Comma separated string of Field names
 * @param {number} [horizontal] If 1, horizontally displayed. If omitted, vertical display is used
 * @param {boolean} [showHeaders] Display headers
 * @returns {string[][]} A list of all funds
 * @param {CustomFunctions.Invocation} invocation Invocation object.
 * @requiresAddress
 */
export async function funds(assetId, properties, horizontal, showHeaders, invocation) {
  try {
    await Utils.prepareOpenAPIClient();

    const { FundAPI } = await import("../apiclient/fund.js");

    let toFetchAssetFunds = false;
    if (properties) {
      let assetFundFields = Utils.assetFundFields;

      let propertyArray = properties.flat(1);
      propertyArray = propertyArray.map((property) => property.split(",")).flat(1);
      propertyArray = propertyArray.map((property) => property.toLowerCase());

      for (let property of propertyArray) {
        if (assetFundFields.includes(property)) {
          toFetchAssetFunds = true;
          break;
        }
      }
    }

    let funds = [];
    if (assetId) {
      let context = Utils.buildContext(null, assetId, null, null, null, null, null);
      funds = await FundAPI.getFunds(context, toFetchAssetFunds);
    } else {
      funds = await FundAPI.getFundsOfUser();
    }

    if (!Boolean(funds?.length)) throw ErrorCode.DataNotFound;

    properties = Utils.handleProperties(properties);

    MixpanelTracker.trackFunction({ functionName: FWFunction.funds, status: "success", cellAddress: invocation.address });

    return Utils.formatArray(funds, properties, horizontal, showHeaders);
  } catch (err) {
    return ErrorHandlerUtil.errorHandler(err, FWFunction.funds, invocation.address);
  }
}

/**
 * List all leads for a fund
 * @customfunction
 * @helpUrl https://fundwave.com/guide/reporting/Lead+Functions
 * @param {number} [fundId] Fund ID to filter leads
 * @param {string[][]} [properties] Comma separated string of field names. Supported fields: id, name, contactEmail, stageId, status
 * @param {number} [horizontal] If 1, horizontally displayed. If omitted, vertical display is used
 * @param {boolean} [showHeaders] Display headers
 * @returns {string[][]} A list of all leads for the specified fund
 * @param {CustomFunctions.Invocation} invocation Invocation object.
 * @requiresAddress
 */
export async function leads(fundId, properties, horizontal, showHeaders, invocation) {
  try {
    if(!fundId) throw ErrorCode.MissingRequiredProps;
    await Utils.prepareOpenAPIClient();

    const { LeadAPIClient } = await Utils.prepareOpenAPIClient(Utils.APIClient.lead);

    const propertyArray = Array.isArray(properties) ? properties.flat(1).join(",").split(",") : [];
    const fieldsSet = new Set(Utils.leadFields?.map((field) => field.name) || []);
    const toFetchCustomFields = properties && propertyArray.some((property) => !fieldsSet.has(property));
    fieldsSet.add("name");
    fieldsSet.add("stageId");

    const unsupportedProperties = propertyArray.map(p => p.trim()).filter((property) => !fieldsSet.has(property));
    if (unsupportedProperties.length > 0) {
      return [[`Please enter only supported fields. Unsupported properties: ${unsupportedProperties.join(", ")}. Supported fields: ${Array.from(fieldsSet).join(", ")}`]];
    }
    
    const { team } = Utils.getMicroservicePayload();
    const requestBody = {
      context: { type: "FUND", id: [fundId.toString()] },
      team: { id: team.id, type: team.type }
    };

    let leads = await LeadAPIClient.getLeads(requestBody);

    if (!Boolean(leads?.length)) throw ErrorCode.DataNotFound;
    if (toFetchCustomFields) leads = await Utils.prepareCustomFieldValues({ type: "FUND", id: fundId.toString() }, leads, propertyArray, undefined, { type: "LEAD", id: leads.map((lead) => lead.id.toString()) });

    properties = Utils.handleProperties(properties);

    MixpanelTracker.trackFunction({ functionName: FWFunction.leads, status: "success", cellAddress: invocation.address });

    return Utils.formatArray(leads, properties, horizontal, showHeaders);
  } catch (err) {
    return ErrorHandlerUtil.errorHandler(err, FWFunction.leads, invocation.address);
  }
}

/**
 * Get details of a lead
 * @customfunction
 * @helpUrl https://fundwave.com/guide/reporting/Lead+Functions
 * @param {string} [leadId] Lead ID to filter leads
 * @param {string[][]} [properties] Comma separated string of Field names
 * @param {number} [horizontal] If 1, horizontally displayed. If omitted, vertical display is used
 * @param {boolean} [showHeaders] Display headers
 * @returns {string[][]} Details of the specified lead
 * @param {CustomFunctions.Invocation} invocation Invocation object.
 * @requiresAddress
 */
export async function lead(leadId, properties, horizontal, showHeaders, invocation) {
  try {
    if(!leadId) throw ErrorCode.MissingRequiredProps;
    await Utils.prepareOpenAPIClient();
    const { LeadAPIClient } = await Utils.prepareOpenAPIClient(Utils.APIClient.lead);

    const propertyArray = Array.isArray(properties) ? properties.flat(1).join(",").split(",") : [];
    const fieldsSet = new Set(Utils.leadFields?.map((field) => field.name) || []);
    const toFetchCustomFields = properties && propertyArray.some((property) => !fieldsSet.has(property));

    let lead = await LeadAPIClient.getLeadById(leadId.toString());
    if (!lead) throw ErrorCode.DataNotFound;
    const fundId = lead?.context.id;
    lead = [lead];
    
    if (toFetchCustomFields) lead = await Utils.prepareCustomFieldValues({ type: "FUND", id: fundId.toString() }, lead, propertyArray, undefined, { type: "LEAD", id: [leadId.toString()] });
    properties = Utils.handleProperties(properties);

    MixpanelTracker.trackFunction({ functionName: FWFunction.lead, status: "success", cellAddress: invocation.address });

    return Utils.formatArray(lead, properties, horizontal, showHeaders);
  } catch (err) {
    return ErrorHandlerUtil.errorHandler(err, FWFunction.lead, invocation.address);
  }
}

/**
 * Get details of an investor
 * @customfunction
 * @helpUrl https://fundwave.com/guide/reporting/Investor+Functions#41b43efa1af046238b07046acdb04bc0
 * @param {number} [investorId] Investor ID
 * @param {number} [fundId] Fund ID, to fetch investor details specific to a fund
 * @param {string[][]} [properties] Comma separated string of Field names
 * @param {number} [horizontal] If 1, horizontally displayed. If omitted, vertical display is used
 * @param {boolean} [showHeaders] Display headers
 * @returns {string[][]} Details of the investor
 * @param {CustomFunctions.Invocation} invocation Invocation object.
 * @requiresAddress
 */
export async function investor(investorId, fundId, properties, horizontal, showHeaders, invocation) {
  try {
    if (!investorId) throw ErrorCode.MissingRequiredProps;

    const { InvestorAPIClient } = await Utils.prepareOpenAPIClient(Utils.APIClient.fundwave);
    const { InvestorAPI } = await import("../apiclient/investor.js");
    const { BankAPI } = await import("../apiclient/bank.js");
    const { ContactAPI } = await import("../apiclient/contact.js");

    const fundIds = fundId ? [fundId] : null;
    let investor = await InvestorAPIClient.getInvestors(fundIds, [investorId]);

    if (!Boolean(investor?.length)) throw ErrorCode.DataNotFound;

    investor = investor[0];
    investor = {
      ...investor,
      investorType: {
        category: investor.type?.category || "",
        investorTypeName: investor.type?.name || "",
        investorType: investor.type?.type || ""
      },
      country: {
        name: investor.country?.name || "",
        code: investor.country?.code || "",
        countryCode: investor.country?.code || ""
      },
      partnershipIds: investor.partnerships?.map(({id}) => id)?.join(","),
      partnershipNames: investor.partnerships?.map(({name}) => name)?.join(","),
      fundIds: investor.partnerships?.map(({fund}) => fund?.id)?.join(","),
      fundNames: investor.partnerships?.map(({fund}) => fund?.name)?.join(",")
    };

    if (!fundId && investor.partnerships?.length === 1) {
      fundId = investor.partnerships?.[0]?.fund?.id;
    }

    let toFetchInvestorBankDetails = false,
      toFetchInvestorContactDetails = false,
      toFetchInvestorTaxDetails = false;
    if (properties) {
      let investorBankFields = Utils.investorBankFields.map((field) => field.name.toLowerCase());
      let investorContactFields = Utils.investorContactFields.map((field) => field.name.toLowerCase());
      let investorTaxFields = Utils.investorTaxFields.map((field) => field.name.toLowerCase());

      let propertyArray = properties.flat(1);
      propertyArray = propertyArray.map((property) => property.split(",")).flat(1);
      propertyArray = propertyArray.map((property) => property.toLowerCase());

      for (let property of propertyArray) {
        if (investorBankFields.includes(property)) toFetchInvestorBankDetails = true;
        if (investorContactFields.includes(property)) toFetchInvestorContactDetails = true;
        if (investorTaxFields.includes(property)) toFetchInvestorTaxDetails = true;
      }
    }
    const propertyArray = Array.isArray(properties) ? properties.flat(1).join(",").split(",") : [];
    const fieldsSet = new Set(Utils.investorFields.map((field) => field.name));
    const toFetchCustomFields = properties && propertyArray.some((property) => !fieldsSet.has(property));

    let investorBankDetails = {},
      investorContactDetails = {},
      investorTaxDetails = {};
    const context =[{
      id: investorId,
      type: "INVESTOR",
      secondaryId: fundId,
      secondaryType: fundId ? "FUND" : undefined
    }]
    if (toFetchInvestorBankDetails) {
      investorBankDetails = await BankAPI.getBankDetails(context);
      const beneficiaryAccount = investorBankDetails?.[0]?.beneficiaryAccount;
      const accountHolderAddress = investorBankDetails?.[0]?.accountHolderAddress;
      const correspondentAccount = investorBankDetails?.[0]?.correspondentAccount;


      investor = {
        ...investor,
        beneficiary: beneficiaryAccount?.accountHolderName,
        bank: beneficiaryAccount?.bank?.name || "",
        accountNumber: beneficiaryAccount?.accountNumber,
        bankAccountId: investorBankDetails?.[0]?.id,
        iBAN: beneficiaryAccount?.iban,
        bankBranch: {
          name: beneficiaryAccount?.bank?.branchName || "",
          swift: beneficiaryAccount?.bank?.swift || "",
          branchCode: beneficiaryAccount?.bank?.branchCode || "",
          branchCodeType: beneficiaryAccount?.bank?.branchCodeType || ""
        },
        bankAddress: beneficiaryAccount?.bank?.address || "",
        reference: investorBankDetails?.[0]?.reference || "",
        accountHolderAddress: {
          firstLine: accountHolderAddress?.firstLine || "",
          secondLine: accountHolderAddress?.secondLine || "",
          city: accountHolderAddress?.city || "",
          state: accountHolderAddress?.state || "",
          country: accountHolderAddress?.country?.name || "",
          postalCode: accountHolderAddress?.postalCode || ""
        },
        correspondentBank: correspondentAccount?.bank?.name || "",
        correspondentAccountNumber: correspondentAccount?.accountNumber,
        correspondentIBAN: correspondentAccount?.iban,
        correspondentBankBranch: {
          name: correspondentAccount?.bank?.branchName || "",
          swift: correspondentAccount?.bank?.swift || "",
          branchCode: correspondentAccount?.bank?.branchCode || "",
          branchCodeType: correspondentAccount?.bank?.branchCodeType || ""
        },
        correspondentBankAddress: correspondentAccount?.bank?.address || "",
      };

      if (beneficiaryAccount?.bank?.branchCodeType === "ABA" || beneficiaryAccount?.bank?.branchCodeType === "Routing Number")
        investor.bankBranch.aba = beneficiaryAccount?.bank?.branchCode;
    }

    if (toFetchInvestorContactDetails) {
      investorContactDetails = await ContactAPI.getContactDetails(context);
      if (investorContactDetails)
        investor = {
          ...investor,
          email: investorContactDetails.email,
          phone: investorContactDetails.phone,
          contactAddress: {
            firstLine: investorContactDetails.contactAddress?.firstLine || "",
            secondLine: investorContactDetails.contactAddress?.secondLine || "",
            city: investorContactDetails.contactAddress?.city || "",
            state: investorContactDetails.contactAddress?.state || "",
            country: investorContactDetails.contactAddress?.country?.name || "",
            postalCode: investorContactDetails.contactAddress?.postalCode || ""
          },
          registeredAddress: {
            firstLine: investorContactDetails.registeredAddress?.firstLine || "",
            secondLine: investorContactDetails.registeredAddress?.secondLine || "",
            city: investorContactDetails.registeredAddress?.city || "",
            state: investorContactDetails.registeredAddress?.state || "",
            country: investorContactDetails.registeredAddress?.country?.name || "",
            postalCode: investorContactDetails.registeredAddress?.postalCode || ""
          }
        };
    }

    if (toFetchInvestorTaxDetails) {
      try {
        //to tackle null api response
        investorTaxDetails = await InvestorAPI.retrieveInvestorDetails(investorId, fundId);
      } catch (error) {
        investorTaxDetails = {};
      }

      investor = {
        ...investor,
        legalName: investorTaxDetails?.legalName,
        identificationType: investorTaxDetails?.entityIdType,
        registration: investorTaxDetails?.entityId,
        entityType: investorTaxDetails?.entityType,
        birthDate: investorTaxDetails?.birthDate,
        residenceTaxId: investorTaxDetails?.taxIdInTaxResidence,
        domicileTaxId: investorTaxDetails?.taxIdInDomicile,
        taxResidence: {
          countryName: investorTaxDetails?.taxResidence?.name,
          countryCode: investorTaxDetails?.taxResidence?.countryCode
        }
      };
    }

    if (toFetchCustomFields) {
      [investor] = await Utils.prepareCustomFieldValues({ type: "INVESTOR" }, [investor], propertyArray);
    }

    properties = Utils.handleProperties(properties);

    MixpanelTracker.trackFunction({ functionName: FWFunction.investor, status: "success", cellAddress: invocation.address });

    return Utils.formatArray([investor], properties, horizontal, showHeaders);
  } catch (err) {
    return ErrorHandlerUtil.errorHandler(err, FWFunction.investor, invocation.address);
  }
}

/**
 * List all investors
 * @customfunction
 * @helpUrl https://fundwave.com/guide/reporting/Investor+Functions#55c4cac58ba5420b93bcbdcfdd7b46a4
 * @param {string[][]} [fundIds] Fund Ids, to fetch investors of a fund
 * @param {string[][]} [properties] Comma separated string of Field names
 * @param {string} [groupBy] Group by options: fund
 * @param {number} [horizontal] If 1, horizontally displayed. If omitted, vertical display is used
 * @param {boolean} [showHeaders] Display headers
 * @returns {string[][]} A list of all investors
 * @param {CustomFunctions.Invocation} invocation Invocation object.
 * @requiresAddress
 */
export async function investors(fundIds, properties, groupBy, horizontal, showHeaders, invocation) {
  try {
    await Utils.prepareOpenAPIClient();

    const { InvestorAPIClient } = await Utils.prepareOpenAPIClient(Utils.APIClient.fundwave);


    const propertyArray = Array.isArray(properties) ? properties.flat(1).join(",").split(",") : [];
    const fieldsSet = new Set(Utils.investorsFields.map((field) => field.name));
    const toFetchCustomFields = properties && propertyArray.some((property) => !fieldsSet.has(property));

    if (fundIds) fundIds = Utils.convert2DArrayto1DArray(fundIds);
    let investors = await InvestorAPIClient.getInvestors(fundIds);

    if (!Boolean(investors?.length)) throw ErrorCode.DataNotFound;

    investors = investors.map((inv) => ({
      ...inv,
      investorType: {
        category: inv.type?.category || "",
        investorTypeName: inv.type?.name || "",
        investorType: inv.type?.type || ""
      },
      country: {
        name: inv.country?.name || "",
        code: inv.country?.code || "",
        countryCode: inv.country?.code || ""
      },
      partnershipIds: inv.partnerships?.map(({ id }) => id)?.join(","),
      partnershipNames: inv.partnerships?.map(({ name }) => name)?.join(","),
      fundIds: inv.partnerships?.map(({ fund }) => fund?.id)?.join(","),
      fundNames: inv.partnerships?.map(({ fund }) => fund?.name)?.join(",")
    }));

   if (groupBy?.toLowerCase() === "fund") {
      investors = investors.flatMap((inv) => {
        // If investor has partnerships, create one row per unique fund
        if (inv.partnerships?.length > 0) {
          // Group partnerships by fund ID to avoid duplicates
          const fundMap = new Map();
          
          inv.partnerships.forEach((partnership) => {
            const fundId = partnership.fund?.id;
            if (fundId) {
              if (!fundMap.has(fundId)) {
                fundMap.set(fundId, {
                  partnershipIds: [partnership.id || ""],
                  partnershipNames: [partnership.name || ""],
                  fundIds: partnership.fund?.id || "",
                  fundNames: partnership.fund?.name || ""
                });
              } else {
                // Add this partnership to the existing fund entry
                const existing = fundMap.get(fundId);
                existing.partnershipIds.push(partnership.id || "");
                existing.partnershipNames.push(partnership.name || "");
              }
            }
          });
          
          // Convert map to array of investor rows, joining partnership arrays
          return Array.from(fundMap.values()).map((fundData) => ({
            ...inv,
            ...fundData,
            partnershipIds: fundData.partnershipIds.join(","),
            partnershipNames: fundData.partnershipNames.join(",")
          }));
        }
        return [];
      });
    }
    if (toFetchCustomFields) investors = await Utils.prepareCustomFieldValues({ type: "INVESTOR" }, investors, propertyArray);

    properties = Utils.handleProperties(properties);

    MixpanelTracker.trackFunction({ functionName: FWFunction.investors, status: "success", cellAddress: invocation.address });

    return Utils.formatArray(investors, properties, horizontal, showHeaders);
  } catch (err) {
    return ErrorHandlerUtil.errorHandler(err, FWFunction.investors, invocation.address);
  }
}

/**
 * List all notice groups
 * @customfunction
 * @helpUrl https://fundwave.com/guide/reporting/Notice+Group+Functions#1c8d333fee154aa18d0e4dd5711712b2
 * @param {number} [fundId] Fund ID. To fetch notice groups of a fund.
 * @param {number} [fromDate] To fetch notice groups from a particular date.
 * @param {number} [toDate] To fetch notice groups before a particular date.
 * @param {string[][]} [properties] Comma separated string of Field names
 * @param {number} [horizontal] If 1, horizontally displayed. If omitted, vertical display is used
 * @param {boolean} [showHeaders] Display headers
 * @param {boolean} [uniqueDueDate] if true, combine notice groups with same due date.
 * @returns {string[][]} A list of all notice groups
 * @param {CustomFunctions.Invocation} invocation Invocation object.
 * @requiresAddress
 */
export async function noticegroups(fundId, fromDate, toDate, properties, horizontal, showHeaders, uniqueDueDate, invocation) {
  try {
    await Utils.prepareOpenAPIClient();

    if (!fundId) throw ErrorCode.MissingRequiredProps;

    const { NoticeAPI } = await import("../apiclient/notice.js");

    const noticeGroups = (await NoticeAPI.getNoticeGroups(fundId, fromDate, toDate, uniqueDueDate)) ?? [];

    if (!Boolean(noticeGroups?.length)) throw ErrorCode.DataNotFound;

    properties = Utils.handleProperties(properties);

    MixpanelTracker.trackFunction({ functionName: FWFunction.noticeGroups, status: "success", cellAddress: invocation.address });

    return Utils.formatArray(noticeGroups, properties, horizontal, showHeaders);
  } catch (err) {
    return ErrorHandlerUtil.errorHandler(err, FWFunction.noticeGroups, invocation.address);
  }
}

/**
 * Get details of a vendor
 * @customfunction
 * @helpUrl https://fundwave.com/guide/reporting/Accounting+Functions/#2d1dc448e93f808a9157ebcb2bfa5ed6
 * @param {number} [vendorId] Vendor ID
 * @param {string[][]} [properties] Comma separated string of Field names
 * @param {number} [horizontal] If 1, horizontally displayed. If omitted, vertical display is used
 * @param {boolean} [showHeaders] Display headers
 * @returns {string[][]} Details of the vendor
 * @param {CustomFunctions.Invocation} invocation Invocation object.
 * @requiresAddress
 */
export async function vendor(vendorId, properties, horizontal, showHeaders, invocation) {
  try {
    if (!vendorId) throw ErrorCode.MissingRequiredProps;
    const { TransactionAPIClient } = await Utils.prepareOpenAPIClient(Utils.APIClient.fundwave);

    const propertyArray = Array.isArray(properties) ? properties.flat(1).join(",").split(",") : [];
    const fieldsSet = new Set(Utils.vendorFields.map((field) => field.name));
    const toFetchCustomFields = properties && propertyArray.some((property) => !fieldsSet.has(property));

    const context = { analysisCodeIds: [vendorId] };

    let vendors = await TransactionAPIClient.getAnalysisCodes(context);

    if (!Boolean(vendors?.length)) throw ErrorCode.DataNotFound;

    if (toFetchCustomFields) vendors = await Utils.prepareCustomFieldValues({ type: "VENDOR" }, vendors, propertyArray);

    properties = Utils.handleProperties(properties);

    MixpanelTracker.trackFunction({ functionName: FWFunction.vendor, status: "success", cellAddress: invocation.address });

    return Utils.formatArray(vendors, properties, horizontal, showHeaders);
  } catch (err) {
    return ErrorHandlerUtil.errorHandler(err, FWFunction.vendor, invocation.address, { vendorId: !!vendorId });
  }
}

/**
 * List all vendors
 * @customfunction
 * @helpUrl https://fundwave.com/guide/reporting/Accounting+Functions/#2d1dc448e93f8083a4c0d73f77d0c851
 * @param {string[][]} [properties] Comma separated string of Field names
 * @param {string[][]} [fundIds] Fund Ids, to fetch vendors used in these funds
 * @param {number} [horizontal] If 1, horizontally displayed. If omitted, vertical display is used
 * @param {boolean} [showHeaders] Display headers
 * @returns {string[][]} A list of all vendors
 * @param {CustomFunctions.Invocation} invocation Invocation object.
 * @requiresAddress
 */
export async function vendors(properties, fundIds, horizontal, showHeaders, invocation) {
  try {
    const { TransactionAPIClient } = await Utils.prepareOpenAPIClient(Utils.APIClient.fundwave);

    const propertyArray = Array.isArray(properties) ? properties.flat(1).join(",").split(",") : [];
    const fieldsSet = new Set(Utils.vendorFields.map((field) => field.name));
    const toFetchCustomFields = properties && propertyArray.some((property) => !fieldsSet.has(property));

    const context = {};
    if (fundIds) context["fundIds"] = Utils.convert2DArrayto1DArray(fundIds);

    let vendors = await TransactionAPIClient.getAnalysisCodes(context);

    if (!Boolean(vendors?.length)) throw ErrorCode.DataNotFound;

    if (toFetchCustomFields) vendors = await Utils.prepareCustomFieldValues({ type: "VENDOR" }, vendors, propertyArray);

    properties = Utils.handleProperties(properties);

    MixpanelTracker.trackFunction({ functionName: FWFunction.vendors, status: "success", cellAddress: invocation.address });

    return Utils.formatArray(vendors, properties, horizontal, showHeaders);
  } catch (err) {
    return ErrorHandlerUtil.errorHandler(err, FWFunction.vendors, invocation.address);
  }
}

/**
 * Returns all journal lines.
 * @customfunction
 * @helpUrl https://fundwave.com/guide/reporting/Accounting+Functions#f8e2e841bbbe4186ada15fe062738192
 * @param {string[][]} [accountCodes] Account Codes, to filter journal entries
 * @param {string[][]} [assetIds] Asset Ids, to filter journal entries
 * @param {string[][]} [assetAccountIds] Asset Account Ids, to filter journal entries
 * @param {string[][]} [investorIds] Investor Ids, to filter journal entries
 * @param {string[][]} [fundIds] Fund Ids, to filter journal entries
 * @param {number} [toDate] to filter journal entries
 * @param {number} [fromDate] to filter journal entries
 * @param {string[][]} [properties] Comma separated string of Field names
 * @param {number} [horizontal] If 1, horizontally displayed. If omitted, vertical display is used.
 * @param {boolean} [showHeaders] Display headers
 * @param {string} [sortByDate] Sort by journal date. "asc"/"desc" for ascending and descending order respectively.
 * @returns {string[][]} A list of journal lines.
 * @param {CustomFunctions.Invocation} invocation Invocation object.
 * @requiresAddress
 */
export async function journallines(accountCodes, assetIds, assetAccountIds, investorIds, fundIds, toDate, fromDate, properties, horizontal, showHeaders, sortByDate, invocation) {
  try {
    if (!Boolean(accountCodes?.length) && !Boolean(assetIds?.length) && !Boolean(assetAccountIds?.length) && !Boolean(investorIds?.length) && !Boolean(fundIds?.length))
      throw ErrorCode.MissingOneOfProps;

    const { JournalAPIClient } = await Utils.prepareOpenAPIClient(Utils.APIClient.fundwave);

    let context = {};
    if (accountCodes) context.accountCodes = Utils.convert2DArrayto1DArray(accountCodes);
    if (assetIds) context.assetIds = Utils.convert2DArrayto1DArray(assetIds);
    if (assetAccountIds) context.assetAccountIds = Utils.convert2DArrayto1DArray(assetAccountIds);
    if (investorIds) context.investorIds = Utils.convert2DArrayto1DArray(investorIds);
    if (fundIds) context.fundIds = Utils.convert2DArrayto1DArray(fundIds);
    if (toDate) context.toDate = Utils.getFormattedDate(toDate).split("T")[0];
    if (fromDate) context.fromDate = Utils.getFormattedDate(fromDate).split("T")[0];



    const journals = (await JournalAPIClient.getJournals(context)) || [];

    let journalLines = journals.flatMap(journal => {
      const { journalLines: lines = [], ...journalWithoutLines } = journal;
      return (lines || []).map(journalLine => ({
        ...journalLine,
        journal: journalWithoutLines
      }));
    });

    journalLines = journalLines.filter((journalLine) => journalLine?.journal?.status?.id == 5);

    journalLines = journalLines.map((journalLine) => {
      return {
        ...journalLine,
        fund: journalLine.journal?.fund,
        partnership: journalLine.journal?.partnership
      };
    });

    if (!Boolean(journalLines?.length)) throw ErrorCode.DataNotFound;

    if (sortByDate == "desc") journalLines.sort((a, b) => new Date(b.journal?.date) - new Date(a.journal?.date));
    else if (sortByDate == "asc") journalLines.sort((a, b) => new Date(a.journal?.date) - new Date(b.journal?.date));

    properties = Utils.handleProperties(properties, "description");

    MixpanelTracker.trackFunction({ functionName: FWFunction.journalLines, status: "success", cellAddress: invocation.address });

    return Utils.formatArray(journalLines, properties, horizontal, showHeaders, "journalLines");
  } catch (err) {
    return ErrorHandlerUtil.errorHandler(err, FWFunction.journalLines, invocation.address);
  }
}

/**
 * Returns all fund transactions.
 * @customfunction
 * @helpUrl https://fundwave.com/guide/reporting/Fund+Transaction+Functions#15ed145855144dc68b30ca2aa0df5a20
 * @param {string[][]} [properties] Comma separated string of field names. Defaults to amount.
 * @param {string[][]} [types] Types, to filter transactions
 * @param {number} [toDate] to filter transactions
 * @param {number} [fromDate] to filter transactions
 * @param {string[][]} [fundIds] Fund Ids, to filter transactions
 * @param {string[][]} [assetIds] Asset Ids, to filter transactions
 * @param {string[][]} [assetAccountIds] AssetAccount Ids, to filter transactions
 * @param {string[][]} [balanceAccountNames] Balance Account Names, to filter transactions
 * @param {number} [horizontal] If 1, horizontally displayed. If omitted, vertical display is used.
 * @param {boolean} [showHeaders] Display headers
 * @returns {string[][]} A list of fund transactions.
 * @param {CustomFunctions.Invocation} invocation Invocation object.
 * @requiresAddress
 */
export async function fundtransactions(properties, types, toDate, fromDate, fundIds, assetIds, assetAccountIds, balanceAccountNames, horizontal, showHeaders, invocation) {
  try {
    if (!Boolean(assetIds?.length) && !Boolean(assetAccountIds?.length) && !Boolean(fundIds?.length)) throw ErrorCode.MissingOneOfProps;

    const { TransactionAPIClient } = await Utils.prepareOpenAPIClient(Utils.APIClient.fundwave);

    let context = { status: 5 };
    if (assetIds) context.assetIds = Utils.convert2DArrayto1DArray(assetIds);
    if (assetAccountIds) context.assetAccountIds = Utils.convert2DArrayto1DArray(assetAccountIds);
    if (fundIds) context.fundIds = Utils.convert2DArrayto1DArray(fundIds);
    if (toDate) context.toDate = Utils.getFormattedDate(toDate).split("T")[0];
    if (fromDate) context.fromDate = Utils.getFormattedDate(fromDate).split("T")[0];
    if (types) {
      types = Utils.convert2DArrayto1DArray(types);
      context.transactionTypeIds = types.map((type) => type.toUpperCase().trim());
    }

    const transactions = (await TransactionAPIClient.getFundTransactions(context)) || [];

    // Filter by balance account names if provided
    let filteredTransactions = transactions;
    if (balanceAccountNames && balanceAccountNames.length) {
      const normalizedNames = new Set(
         Utils.convert2DArrayto1DArray(balanceAccountNames).map((name) =>
           name.toString().trim().toLowerCase().replace(/[\s]/g, '')
         )
        );
      filteredTransactions = transactions.filter((txn) => {
        const normalizedTransactionName = (txn?.balanceAccountCode?.accountCode?.name ?? '').toLowerCase().replace(/[\s]/g, '');
        return normalizedNames.has(normalizedTransactionName);
      });
    }
    
    if (!Boolean(filteredTransactions?.length)) throw ErrorCode.DataNotFound;

    properties = Utils.handleProperties(properties, "amount");

    MixpanelTracker.trackFunction({ functionName: FWFunction.fundTransactions, status: "success", cellAddress: invocation.address });

    return Utils.formatArray(filteredTransactions, properties, horizontal, showHeaders, "transactions");
  } catch (err) {
    return ErrorHandlerUtil.errorHandler(err, FWFunction.fundTransactions, invocation.address);
  }
}

/**
 * Get fund transaction types
 * @customfunction
 * @helpUrl https://fundwave.com/guide/reporting/Fund+Transaction+Functions#fe858322b7ce4229bc6cfbde0daa9e64
 * @param {string[][]} [properties] Comma separated string of field names. Defaults to 'type'.
 * @param {number} [fundId] Fund ID. To fetch transaction types of a fund.
 * @param {number} [horizontal] If 1, horizontally displayed. If omitted, vertical display is used
 * @param {boolean} [showHeaders] Display headers
 * @returns {string[][]} A list of fund transaction types
 * @param {CustomFunctions.Invocation} invocation Invocation object.
 * @requiresAddress
 */
export async function fundtransactiontypes(properties, fundId, horizontal, showHeaders, invocation) {
  try {
    await Utils.prepareOpenAPIClient();

    if (!fundId) throw ErrorCode.MissingRequiredProps;

    const { TransactionAPI } = await import("../apiclient/transaction.js");

    const transactionTypes = await TransactionAPI.geTransactionTypes(fundId);

    if (!Boolean(transactionTypes?.length)) throw ErrorCode.DataNotFound;

    properties = Utils.handleProperties(properties, "type");

    MixpanelTracker.trackFunction({ functionName: FWFunction.fundTransactionTypes, status: "success", cellAddress: invocation.address });

    return Utils.formatArray(transactionTypes, properties, horizontal, showHeaders);
  } catch (err) {
    return ErrorHandlerUtil.errorHandler(err, FWFunction.fundTransactionTypes, invocation.address);
  }
}

/**
 * A list of properties for an entity
 * @customfunction
 * @helpUrl https://fundwave.com/guide/reporting/Properties+Function#2d2e9663b9b145ffa8debab6cfa69f57
 * @param {string} [entity] entity name. eg- asset, fund, investor, round, noticegroup, deal, captable, shareholder, vendor, lead.
 * @param {number} [horizontal] If 1, horizontally displayed. If omitted, vertical display is used.
 * @param {string} [entityId] Entity ID. eg- fundId 
 * @returns {string[][]} A list of properties.
 * @param {CustomFunctions.Invocation} invocation Invocation object.
 * @requiresAddress
 */
export async function properties(entity, horizontal, entityId, invocation) {
  try {
    await Utils.prepareOpenAPIClient();

    if (!entity) throw ErrorCode.MissingRequiredProps;

    let fields = [];

    entity = entity.toLowerCase();

    if (["deal", "deals"].includes(entity)) fields = await getDealProperties();
    else if (["asset", "assets"].includes(entity)) fields = await getAssetProperties(entity);
    else if (["investor", "investors"].includes(entity)) fields = await getInvestorProperties(entity);
    else if (["vendor", "vendors"].includes(entity)) fields = await getVendorProperties();
    else if (["lead", "leads"].includes(entity)) {
      if (!entityId) return [["Please supply fundId"]];
      fields = await getLeadProperties(entityId);
    }
    else if (entity === "dealscount") {
      fields = await getDealProperties();
      fields = fields.filter((field) => ["dropdown", "radio"].includes(field.type));
    } else fields = Utils.fields?.[entity] || [];

    if (!Boolean(fields?.length)) throw ErrorCode.DataNotFound;

    MixpanelTracker.trackFunction({ functionName: FWFunction.properties, status: "success", cellAddress: invocation.address });

    return Utils.formatArray(fields, "name", horizontal, false);
  } catch (err) {
    return ErrorHandlerUtil.errorHandler(err, FWFunction.properties, invocation.address);
  }
}

/**
 * List details of all the deals user has access to.
 * @customfunction
 * @helpUrl https://fundwave.com/guide/reporting/Dealflow+Functions#b76e18594f7e46c99df70744ac3aa109
 * @param {string[][]} [properties] Comma separated string of Field names
 * @param {string} [statusFilter] Comma separated string of active, invested, dropped, hold
 * @param {number} [horizontal] If 1, horizontally displayed. If omitted, vertical display is used.
 * @param {boolean} [showHeaders] Display headers
 * @returns {string[][]} A list of properties of all deals.
 * @param {CustomFunctions.Invocation} invocation Invocation object.
 * @requiresAddress
 */
export async function deals(properties, statusFilter, horizontal, showHeaders, invocation) {
  try {
    await Utils.prepareOpenAPIClient(Utils.APIClient.note);
    let filter = getDealsFilter(statusFilter);

    const { DealsAPI } = await import("../apiclient/deals.js");

    let result = {};

    if (!properties) properties = [];
    properties = properties.flat(1).join(",");
    properties = properties
      .split(",")
      .map((property) => property.trim())
      .join(",");

    result = await DealsAPI.getDeals(properties, filter);

    if (!result.headers || result.headers.length === 0) return [["No Column Found"]];

    if (!result.deals || result.deals.length === 0) return [["No Data"]];

    let details = [[""]];

    if (!result) throw ErrorCode.DataNotFound;

    details = result.deals.map((deal) => {
      let dealData = [""];
      dealData = result.headers.map((prop) => {
        let value = deal[prop.key] || "";
        if (value && isNaN(value) && moment(value, moment.ISO_8601, true).isValid()) {
          value = Utils.getExcelDateFromJS(value, true);
        } 

        return value;
      });
      return dealData;
    });

    if (showHeaders) {
      const headers = result.headers.map((header) => header.name);
      details.unshift(headers);
    }

    if (horizontal) details = Utils.transposeOfMatrix(details);

    MixpanelTracker.trackFunction({ functionName: FWFunction.deals, status: "success", cellAddress: invocation.address });

    return details;
  } catch (err) {
    return ErrorHandlerUtil.errorHandler(err, FWFunction.deals, invocation.address);
  }
}

/**
 * Aggregate and Count deals on your custom fields.
 * @customfunction
 * @helpUrl https://fundwave.com/guide/reporting/Dealflow+Functions#2c1b8035b3e54fd08c5848e4c25b218a
 * @param {string} [properties] Comma separated string of Field names
 * @param {string} [statusFilter] Comma separated string of active, invested, dropped, hold
 * @param {boolean} [showHeaders] Display headers
 * @returns {string[][]} A list of deals with their aggregates and count.
 * @param {CustomFunctions.Invocation} invocation Invocation object.
 * @requiresAddress
 */
export async function dealscount(properties, statusFilter, showHeaders, invocation) {
  try {
    if (!properties) throw ErrorCode.MissingRequiredProps;

    await Utils.prepareOpenAPIClient(Utils.APIClient.note);

    const { DealsAPI } = await import("../apiclient/deals.js");

    let result = {};

    let filter = getDealsFilter(statusFilter);

    properties = properties
      .split(",")
      .map((property) => property.trim())
      .join(","); //to trim out spaces from individual props

    result = await DealsAPI.getDealsAggregation(properties, filter);

    if (!result.headers || result.headers.length === 0) return [["No Column Found"]];

    if (!result.aggregateResult || Object.keys(result.aggregateResult).length === 0) return [["No Data"]];

    let details = [];

    if (!result) throw ErrorCode.DataNotFound;

    Object.entries(result.aggregateResult).forEach(([column, columnData], index) => {
      let dealData = [];
      dealData = columnData.map((valueCount) => {
        let value = [valueCount.value, valueCount.count];

        return value;
      });
      if (showHeaders) dealData.unshift([result.headerMap[column], ""]);
      if (index !== 0) dealData.unshift(["", ""]);
      details = details.concat(dealData);
    });

    MixpanelTracker.trackFunction({ functionName: FWFunction.dealsCount, status: "success", cellAddress: invocation.address });

    return details;
  } catch (err) {
    return ErrorHandlerUtil.errorHandler(err, FWFunction.dealsCount, invocation.address);
  }
}

/**
 * Get notes of a deal
 * @customfunction
 * @helpUrl https://fundwave.com/guide/reporting/Dealflow+Functions#31ff64222fcd43fc84948b6ce4033ec9
 * @param {string[][]} [dealIds] Deal Ids, whose notes to fetch
 * @param {number} [toDate] notes as of this date
 * @param {number} [fromDate] notes as from date
 * @param {string[][]} [properties] Comma separated string of Field names
 * @param {number} [horizontal] If 1, horizontally displayed. If omitted, vertical display is used
 * @param {boolean} [showHeaders] Display headers
 * @returns {string[][]} A list of notes of a deal
 * @param {CustomFunctions.Invocation} invocation Invocation object.
 * @requiresAddress
 */
export async function dealnotes(dealIds, toDate, fromDate, properties, horizontal, showHeaders, invocation) {
  try {
    if (!Boolean(dealIds?.length) || !toDate) throw ErrorCode.MissingRequiredProps;

    const { NoteAPIClient } = await Utils.prepareOpenAPIClient(Utils.APIClient.note);
    const { DealsAPI } = await import("../apiclient/deals.js");

    dealIds = dealIds.flat(1);
    properties = Utils.handleProperties(properties, "title");

    if (Date.parse(toDate) < Date.parse(fromDate)) return [["Invalid date-range"]];

    const dealNotes = await DealsAPI.getNotes(dealIds, NoteAPIClient);
    if (!Boolean(dealNotes?.length)) throw ErrorCode.DataNotFound;

    const filteredDealNotes = dealNotes?.filter((note, index, array) => {
      const toDateFilter = note.date <= toDate;
      let fromDateFilter = note.date >= fromDate;
      if (!fromDate) {
        fromDateFilter = array.findIndex((element) => element.dealId === note.dealId) === index;
      }

      return toDateFilter && fromDateFilter;
    });

    MixpanelTracker.trackFunction({ functionName: FWFunction.dealNotes, status: "success", cellAddress: invocation.address });
    if (!Boolean(filteredDealNotes?.length)) throw ErrorCode.DataNotFound;
    return Utils.formatArray(filteredDealNotes, properties, horizontal, showHeaders);
  } catch (err) {
    return ErrorHandlerUtil.errorHandler(err, FWFunction.dealNotes, invocation.address, { dealIds: !!dealIds, toDate: !!toDate });
  }
}

/**
 * Get details of a round.
 * @customfunction
 * @helpUrl https://fundwave.com/guide/reporting/Investment+Round+Functions#3d3819ea9f6d420e8ae73932a693b521
 * @param {string[][]} [properties] Comma separated string of field names. Defaults to name.
 * @param {number} [assetId] asset ID
 * @param {number} [toDate] to get the last round details as of this date
 * @param {string} [roundId] round ID
 * @param {number} [horizontal] If 1, horizontally displayed. If omitted, vertical display is used.
 * @param {boolean} [showHeaders] Display headers
 * @returns {string[][]} Details of a round
 * @param {CustomFunctions.Invocation} invocation Invocation object.
 * @requiresAddress
 */
export async function round(properties, assetId, toDate, roundId, horizontal, showHeaders, invocation) {
  try {
    if (!assetId) throw ErrorCode.MissingRequiredProps;

    const { AssetAccountAPIClient } = await Utils.prepareOpenAPIClient(Utils.APIClient.fundwave);
    const { RoundAPIClient } = await Utils.prepareOpenAPIClient(Utils.APIClient.instrument);
    const { AssetAPI } = await import("../apiclient/asset.js");
    const { addInstrumentsNameToRounds } = await import("../utils/common/captable-utils.js");

    const assetAccounts = await AssetAccountAPIClient.getAssetAccounts(undefined, { assetIds: [assetId] });

    let round;

    //roundId is given priority over toDate
    if (roundId) {
      round = await AssetAPI.getRound(roundId, assetId, RoundAPIClient);
    } else if (toDate) {
      toDate = Utils.getFormattedDate(toDate).split("T")[0];
      round = await AssetAPI.getRoundWithDate(assetId, toDate, RoundAPIClient);
    } else {
      const rounds = await AssetAPI.getRounds(assetId, RoundAPIClient);
      round = rounds?.[rounds.length - 1];
    }

    round = addInstrumentsNameToRounds([round], assetAccounts)?.[0];

    if (!round) throw ErrorCode.DataNotFound;

    properties = Utils.handleProperties(properties);

    MixpanelTracker.trackFunction({ functionName: FWFunction.round, status: "success", cellAddress: invocation.address });

    return Utils.formatArray([round], properties, horizontal, showHeaders);
  } catch (err) {
    return ErrorHandlerUtil.errorHandler(err, FWFunction.round, invocation.address);
  }
}

/**
 * List rounds of an asset.
 * @customfunction
 * @helpUrl https://fundwave.com/guide/reporting/Investment+Round+Functions#e43d2d369c2f4e5b98c072336234ea29
 * @param {string[][]} [properties] Comma separated string of field names. Defaults to name.
 * @param {number} [assetId] asset whose rounds to fetch
 * @param {number} [toDate] to filter rounds as on this date
 * @param {number} [horizontal] If 1, horizontally displayed. If omitted, vertical display is used.
 * @param {boolean} [showHeaders] Display headers
 * @returns {string[][]} A list of rounds
 * @param {CustomFunctions.Invocation} invocation Invocation object.
 * @requiresAddress
 */
export async function rounds(properties, assetId, toDate, horizontal, showHeaders, invocation) {
  try {
    if (!assetId) throw ErrorCode.MissingRequiredProps;

    const { AssetAccountAPIClient } = await Utils.prepareOpenAPIClient(Utils.APIClient.fundwave);
    const { RoundAPIClient } = await Utils.prepareOpenAPIClient(Utils.APIClient.instrument);

    const { AssetAPI } = await import("../apiclient/asset.js");
    const { addInstrumentsNameToRounds } = await import("../utils/common/captable-utils.js");

    let rounds = await AssetAPI.getRounds(assetId, RoundAPIClient);
    const assetAccounts = await AssetAccountAPIClient.getAssetAccounts(undefined, { assetIds: [assetId] });

    if (toDate) rounds = Utils.filterByDate(rounds, toDate, null, "rounds");

    if (!Boolean(rounds?.length)) throw ErrorCode.DataNotFound;

    rounds = addInstrumentsNameToRounds(rounds, assetAccounts);
    properties = Utils.handleProperties(properties);

    MixpanelTracker.trackFunction({ functionName: FWFunction.rounds, status: "success", cellAddress: invocation.address });

    return Utils.formatArray(rounds, properties, horizontal, showHeaders);
  } catch (err) {
    return ErrorHandlerUtil.errorHandler(err, FWFunction.rounds, invocation.address);
  }
}

/**
 * List details of all the captables user has access to.
 * @customfunction
 * @helpUrl https://fundwave.com/guide/reporting/Captable+Functions#02ee096925d0476b9f7e322c96a6fd20
 * @param {string[][]} [properties] Comma separated string of Field names
 * @param {number} [horizontal] If 1, horizontally displayed. If omitted, vertical display is used
 * @param {boolean} [showHeaders] Display headers
 * @param {string[][]} [assetIds] Comma separated stirng of Asset Ids
 * @param {number} [toDate] captable as of this date
 * @returns {string[][]} A list of captables
 * @param {CustomFunctions.Invocation} invocation Invocation object.
 * @requiresAddress
 */
export async function captables(properties, horizontal, showHeaders, assetIds, toDate, invocation) {
  try {
    const { AssetAPIClient, AssetAccountAPIClient } = await Utils.prepareOpenAPIClient(Utils.APIClient.fundwave);
    const { ShareholderAPIClient, HoldingAPIClient } = await Utils.prepareOpenAPIClient(Utils.APIClient.instrument);

    if (!!assetIds) assetIds = Utils.convert2DArrayto1DArray(assetIds);

    if (!toDate) toDate = Utils.getJSDateFromExcel(Utils.getExcelDateFromJS(new Date())).split("T")[0];
    else toDate = Utils.getFormattedDate(toDate).split("T")[0];

    properties = Utils.handleProperties(properties);

    let assetsArray = (await AssetAPIClient.getAssets(assetIds)) || [];
    if (!Boolean(assetsArray?.length)) throw ErrorCode.DataNotFound;

    const shouldFetchShareholderDetails = properties
      .split(",")
      .some((prop) => ["shareholderId", "shareholderName", "shares", "nominalShares", "ownership", "fdstake", "amount", "options", "warrants"].includes(prop));

    if (!shouldFetchShareholderDetails) {
      assetsArray = assetsArray.sort((a, b) => (+a.id > +b.id ? 1 : -1));
      assetsArray = assetsArray.map((asset) => {
        return {
          ...asset,
          assetId: +asset.id
        };
      });
      return await Utils.formatArray(assetsArray, properties, horizontal, showHeaders);
    }

    const { getShareholdersFdStakeByAssetData } = await import("../utils/common/captable-utils.js");

    const payload = Utils.getMicroservicePayload(assetIds, "ASSET");
    const captableDataPromise = [
      ShareholderAPIClient.getShareholders({ team: payload.team }),
      HoldingAPIClient.getHoldings(payload),
      AssetAccountAPIClient.getAssetAccounts(undefined, { assetIds: assetIds })
    ];
    let [shareholdersArray, holdingsArray, assetAccountsArray] = await Promise.all(captableDataPromise);
    holdingsArray = holdingsArray.filter((holding) => new Date(holding.date) <= new Date(toDate));

    const prefStocksAssetAccountIds = [];
    for (let assetAccount of assetAccountsArray) {
      if (assetAccount.instrumentType == "PREFERRED_STOCK") prefStocksAssetAccountIds.push(assetAccount.id);
    }
    const preferredStockAssetAccountsArray = await AssetAccountAPIClient.getPreferredStocks(prefStocksAssetAccountIds);

    let shareholdersFdStakeArray = getShareholdersFdStakeByAssetData(shareholdersArray, holdingsArray, preferredStockAssetAccountsArray, assetAccountsArray);

    if (!Boolean(shareholdersFdStakeArray?.length)) return [["No shareholders found"]];

    let orderedCaptables = [];
    let assetsMap = {};
    assetsArray.forEach((asset) => (assetsMap = { ...assetsMap, [asset.id]: asset }));

    if (!!assetIds) {
      assetIds.forEach((assetId) => {
        const assetHoldings = shareholdersFdStakeArray.filter((holding) => holding.context.id === assetId);
        orderedCaptables.push(...assetHoldings);
      });
    } else {
      orderedCaptables = shareholdersFdStakeArray.sort((a, b) => a.context.id - b.context.id);
    }

    orderedCaptables = orderedCaptables.map((shareholder) => ({
      ...shareholder,
      name: assetsMap[shareholder.context.id].name,
      id: assetsMap[shareholder.context.id].id,
      assetId: assetsMap[shareholder.context.id].id,
      country: assetsMap[shareholder.context.id].country
    }));

    MixpanelTracker.trackFunction({ functionName: FWFunction.captables, status: "success", cellAddress: invocation.address });

    return await Utils.formatArray(orderedCaptables, properties, horizontal, showHeaders);
  } catch (err) {
    return ErrorHandlerUtil.errorHandler(err, FWFunction.captables, invocation.address);
  }
}

/**
 * List shareholders of all the captables user has access to.
 * @customfunction
 * @helpUrl https://fundwave.com/guide/reporting/Captable+Functions#3da090c2e9bc40ff9cdbd5495b79511a
 * @param {string[][]} [properties] Comma separated string of Field names
 * @param {number} [horizontal] If 1, horizontally displayed. If omitted, vertical display is used
 * @param {boolean} [showHeaders] Display headers
 * @returns {string[][]} A list of shareholders
 * @param {CustomFunctions.Invocation} invocation Invocation object.
 * @requiresAddress
 */
export async function shareholders(properties, horizontal, showHeaders, invocation) {
  try {
    const { ShareholderAPIClient } = await Utils.prepareOpenAPIClient(Utils.APIClient.instrument);
    const { getShareholderFundId } = await import("../utils/common/captable-utils.js");

    properties = Utils.handleProperties(properties);

    const { team } = Utils.getMicroservicePayload();
    const shareholdersRaw = await ShareholderAPIClient.getShareholders({ team: team });

    if (!Boolean(shareholdersRaw?.length)) throw ErrorCode.DataNotFound;

    const shareholders = shareholdersRaw
      .map((shareholder) => ({
        ...shareholder,
        fundId: getShareholderFundId(shareholder)
      }))
      .sort((a, b) => (a.name < b.name ? -1 : 1));

    MixpanelTracker.trackFunction({ functionName: FWFunction.shareholders, status: "success", cellAddress: invocation.address });

    return await Utils.formatArray(shareholders, properties, horizontal, showHeaders);
  } catch (err) {
    return ErrorHandlerUtil.errorHandler(err, FWFunction.shareholders, invocation.address);
  }
}

/**
 * Calculate FD Stake for a fund of an asset and a particular round or as of any date.
 * @customfunction
 * @helpUrl https://fundwave.com/guide/reporting/Captable+Functions#e0b8eb32fd6b4da4a76e58bf57d4663b
 * @param {number} [assetId] asset whose stake to calculate
 * @param {string} [fundDetail] ID or name of the fund
 * @param {string} [roundDetails] name or date of the round
 * @param {string} [scenarioId] ID of the scenario for forecasted fdstake
 * @returns {string[][]} fdstake of the given asset & investor
 * @param {CustomFunctions.Invocation} invocation Invocation object.
 * @requiresAddress
 */
export async function fdstake(assetId, fundDetail, roundDetails, scenarioId, invocation) {
  try {
    if (!assetId || !fundDetail) throw ErrorCode.MissingRequiredProps;

    const { RoundAPIClient, ShareholderAPIClient } = await Utils.prepareOpenAPIClient(Utils.APIClient.instrument);
    const { VariableAPIClient } = await Utils.prepareOpenAPIClient(Utils.APIClient.fundwave);
    const { getShareholderFundId } = await import("../utils/common/captable-utils.js");

    let toDate = Utils.getFormattedDate(Utils.getExcelDateFromJS(new Date()), false)

    if (roundDetails) {
      if (!isNaN(roundDetails)) {
        //roundDetails is a number (date)
        toDate = Utils.getFormattedDate(roundDetails, false);
      } else {
        // roundDetails is a string (round name), find the matching round
        const payload = Utils.getMicroservicePayload([assetId.toString()], "ASSET");
        const roundsArray = await RoundAPIClient.getRounds(payload);
        const round = scenarioId 
                      ? roundsArray.find((round) => round?.forecastDetails?.scenarioId === scenarioId && round?.name?.toLowerCase() === roundDetails?.toLowerCase()) 
                      : roundsArray.find((round) => round.type === "ACTUAL" && round?.name?.toLowerCase() === roundDetails?.toLowerCase());
        
        if (!round) return [["No round found"]];
        toDate = Utils.getFormattedDate(Utils.getExcelDateFromJS(new Date(round.date)), false);
      }
    } else if (scenarioId) {
      const payload = Utils.getMicroservicePayload([assetId.toString()], "ASSET");
      const roundsArray = (await RoundAPIClient.getRounds(payload) || [])?.filter((round) => round.type === "FORECAST" && round?.forecastDetails?.scenarioId === scenarioId);
      const round = roundDetails ? roundsArray.find(round?.name?.toLowerCase() === roundDetails?.toLowerCase()) : roundsArray?.at(-1);
      if (!round) return [["No round found"]];
      toDate = Utils.getFormattedDate(Utils.getExcelDateFromJS(new Date(round.date)), false);
    }

    let fundId = null;

    if (!isNaN(fundDetail)) {
      //fundDetail is a number (fundId)
      fundId = fundDetail.toString();
    } else {
      //fundDetail is a string (fundName)
      const payload = Utils.getMicroservicePayload([assetId.toString()], "ASSET");
      const shareholdersArray = await ShareholderAPIClient.getShareholders({ team: payload.team });
      const shareholder = shareholdersArray.find((shareholder) => {
        return shareholder?.name?.toLowerCase() === fundDetail?.toLowerCase() && getShareholderFundId(shareholder);
      });

      fundId = getShareholderFundId(shareholder)?.toString();
    }

    if (!fundId) return [["No fund found"]];

    const context = {
      toDate: toDate,
      assetIds: [assetId.toString()],
      fundIds: [fundId],
      ...(scenarioId ? { scenarioId: scenarioId } : {})
    };

    const resultArray = await VariableAPIClient.getVariableValuesByDate({"F_FDSTAKE": context});

    const fdStake = Number(resultArray?.["F_FDSTAKE"]?.[toDate] || 0);

    if (!fdStake) throw ErrorCode.DataNotFound;

    MixpanelTracker.trackFunction({ functionName: FWFunction.fdStake, status: "success", cellAddress: invocation.address });

    return [[fdStake]];
  } catch (err) {
    return ErrorHandlerUtil.errorHandler(err, FWFunction.fdStake, invocation.address, { assetId: !!assetId, fundDetail: !!fundDetail });
  }
}

/**
 * Calculate Pre-Money Valuation for asset(s) as of any date.
 * @customfunction
 * @helpUrl https://fundwave.com/guide/reporting/Captable+Functions#e0b8eb32fd6b4da4a76e58bf57d4663b
 * @param {string[][]} [assetIds] ID of the assets for which pre-money valuation is to be calculated
 * @param {number} [toDate] date as of which pre-money valuation is to be calculated
 * @param {string} [displayCurrency] Currency code in which pre-money valuation is to be displayed
 * @param {string} [scenarioId] ID of the scenario for forecasted pre-money valuation
 * @returns {string[][]} pre-money valuation of the given assets
 * @param {CustomFunctions.Invocation} invocation Invocation object.
 * @requiresAddress
 */
export async function premoney(assetIds, toDate, displayCurrency, scenarioId, invocation) {
  try {
    if (!assetIds || !displayCurrency) throw ErrorCode.MissingRequiredProps;

    if (!InputValidator.isValidCurrencyCode(displayCurrency)) {
      return [[InputValidator.INVALID_CURRENCY_CODE]];
    }

    assetIds = Utils.convert2DArrayto1DArray(assetIds);

    const { VariableAPIClient } = await Utils.prepareOpenAPIClient(Utils.APIClient.fundwave);

    if (toDate && !isNaN(toDate)) {
      toDate = Utils.getFormattedDate(toDate, false);
    } else {
      toDate = Utils.getFormattedDate(Utils.getExcelDateFromJS(new Date()), false);
    }

    const requestContext = {
      toDate: toDate,
      assetIds: assetIds.map((id) => parseInt(id)),
      ...(scenarioId ? { scenarioId: scenarioId } : {}),
      ...(displayCurrency ? { outputCurrencyCode: displayCurrency } : {}),
      codes: ["F_PREMONEY"]
    }

    const result = (await VariableAPIClient.getVariableValues(requestContext))?.[0];

    if (result?.value === null || result?.value === undefined) throw ErrorCode.DataNotFound;

    const preMoneyValuation = Number(result.value);

    MixpanelTracker.trackFunction({ functionName: FWFunction.preMoney, status: "success", cellAddress: invocation.address });

    return [[preMoneyValuation]];
  } catch (err) {
    return ErrorHandlerUtil.errorHandler(err, FWFunction.preMoney, invocation.address, { assetIds: !!assetIds, displayCurrency: !!displayCurrency } );
  }
}

/**
 * Calculate Post-Money Valuation for asset(s) as of any date.
 * @customfunction
 * @helpUrl https://fundwave.com/guide/reporting/Captable+Functions#e0b8eb32fd6b4da4a76e58bf57d4663b
 * @param {string[][]} [assetIds] ID of the assets for which post-money valuation is to be calculated
 * @param {number} [toDate] date as of which post-money valuation is to be calculated
 * @param {string} [displayCurrency] Currency code in which post-money valuation is to be displayed
 * @param {string} [scenarioId] ID of the scenario for forecasted post-money valuation
 * @returns {string[][]} post-money valuation of the given assets
 * @param {CustomFunctions.Invocation} invocation Invocation object.
 * @requiresAddress
 */
export async function postmoney(assetIds, toDate, displayCurrency, scenarioId, invocation) {
  try {
    if (!assetIds || !displayCurrency) throw ErrorCode.MissingRequiredProps;

    if (!InputValidator.isValidCurrencyCode(displayCurrency)) {
      return [[InputValidator.INVALID_CURRENCY_CODE]];
    }

    assetIds = Utils.convert2DArrayto1DArray(assetIds);

    const { VariableAPIClient } = await Utils.prepareOpenAPIClient(Utils.APIClient.fundwave);

    if (toDate && !isNaN(toDate)) {
      toDate = Utils.getFormattedDate(toDate, false);
    } else {
      toDate = Utils.getFormattedDate(Utils.getExcelDateFromJS(new Date()), false);
    }

    const requestContext = {
      toDate: toDate,
      assetIds: assetIds.map((id) => parseInt(id)),
      ...(scenarioId ? { scenarioId: scenarioId } : {}),
      ...(displayCurrency ? { outputCurrencyCode: displayCurrency } : {}),
      codes: ["F_POSTMONEY"]
    }

    const result = (await VariableAPIClient.getVariableValues(requestContext))?.[0];

    if (result?.value === null || result?.value === undefined) throw ErrorCode.DataNotFound;

    const postMoneyValuation = Number(result.value);

    MixpanelTracker.trackFunction({ functionName: FWFunction.postMoney, status: "success", cellAddress: invocation.address });

    return [[postMoneyValuation]];
  } catch (err) {
    return ErrorHandlerUtil.errorHandler(err, FWFunction.postMoney, invocation.address, { assetIds: !!assetIds, displayCurrency: !!displayCurrency });
  }
}

/**
 * Calculate Amount Raised for asset(s) as of any date.
 * @customfunction
 * @helpUrl https://fundwave.com/guide/reporting/Captable+Functions#e0b8eb32fd6b4da4a76e58bf57d4663b
 * @param {string[][]} [assetIds] ID of the assets for which amount raised is to be calculated
 * @param {number} [toDate] date as of which amount raised is to be calculated
 * @param {string} [displayCurrency] Currency code in which amount raised is to be displayed
 * @param {string} [scenarioId] ID of the scenario for forecasted amount raised
 * @returns {string[][]} amount raised of the given assets
 * @param {CustomFunctions.Invocation} invocation Invocation object.
 * @requiresAddress
 */
export async function raised(assetIds, toDate, displayCurrency, scenarioId, invocation) {
  try {
    if (!assetIds || !displayCurrency) throw ErrorCode.MissingRequiredProps;

    if (!InputValidator.isValidCurrencyCode(displayCurrency)) {
      return [[InputValidator.INVALID_CURRENCY_CODE]];
    }

    assetIds = Utils.convert2DArrayto1DArray(assetIds);

    const { VariableAPIClient } = await Utils.prepareOpenAPIClient(Utils.APIClient.fundwave);

    if (toDate && !isNaN(toDate)) {
      toDate = Utils.getFormattedDate(toDate, false);
    } else {
      toDate = Utils.getFormattedDate(Utils.getExcelDateFromJS(new Date()), false);
    }

    const requestContext = {
      toDate: toDate,
      assetIds: assetIds.map((id) => parseInt(id)),
      ...(scenarioId ? { scenarioId: scenarioId } : {}),
      ...(displayCurrency ? { outputCurrencyCode: displayCurrency } : {}),
      codes: ["F_RAISED"]
    };

    const result = (await VariableAPIClient.getVariableValues(requestContext))?.[0];

    if (result?.value === null || result?.value === undefined) throw ErrorCode.DataNotFound;

    const amountRaised = Number(result.value);

    MixpanelTracker.trackFunction({ functionName: FWFunction.raised, status: "success", cellAddress: invocation.address });
    
    return [[amountRaised]];
  } catch (err) {
    return ErrorHandlerUtil.errorHandler(err, FWFunction.raised, invocation.address, { assetIds: !!assetIds, displayCurrency: !!displayCurrency });
  }
}

/**
 * Calculate Valuation for a fund of an asset and a particular round or as of any date.
 * @customfunction
 * @helpUrl https://fundwave.com/guide/reporting/Captable+Functions#e0b8eb32fd6b4da4a76e58bf57d4663b
 * @param {string[][]} [assetIds] ID of the asset for with valuation is to be calculated
 * @param {string[][]} [fundIds] ID of fund for which valuation is to be calculated
 * @param {number} [toDate] date as of which valuation is to be calculated
 * @param {string} [scenarioId] ID of the scenario for forecasted valuation
 * @returns {string[][]} valuation of the given assets or funds
 * @param {CustomFunctions.Invocation} invocation Invocation object.
 * @requiresAddress
 */
export async function valuation(assetIds, fundIds, toDate, scenarioId, invocation) {
  try {
    if (!assetIds && !fundIds) throw ErrorCode.MissingRequiredProps;

    if (fundIds) fundIds = Utils.convert2DArrayto1DArray(fundIds);
    if (assetIds) assetIds = Utils.convert2DArrayto1DArray(assetIds);

    const { RoundAPIClient } = await Utils.prepareOpenAPIClient(Utils.APIClient.instrument);
    const { VariableAPIClient } = await Utils.prepareOpenAPIClient(Utils.APIClient.fundwave);

    if (toDate && !isNaN(toDate)) {
      toDate = Utils.getFormattedDate(toDate, false);
    } else if (scenarioId) {
      const payload = Utils.getMicroservicePayload(assetIds, "ASSET");
      const roundsArray = (await RoundAPIClient.getRounds(payload) || [])?.filter((round) => round.type === "FORECAST" && round?.forecastDetails?.scenarioId === scenarioId);
      const round = roundsArray?.at(-1);
      if (!round) return [["No round found"]];
      toDate = Utils.getFormattedDate(Utils.getExcelDateFromJS(new Date(round.date)), false);
    } else {
      toDate = Utils.getFormattedDate(Utils.getExcelDateFromJS(new Date()), false);
    }

    const context = {
      toDate: toDate,
      assetIds: assetIds || [],
      fundIds: fundIds || [],
      ...(scenarioId ? { scenarioId: scenarioId } : {})
    };

    const resultArray = await VariableAPIClient.getVariableValuesByDate({"F_TOTALVALUE": context});

    const valuation = Number(resultArray?.["F_TOTALVALUE"]?.[toDate]|| 0);

    if (!valuation) throw ErrorCode.DataNotFound;

    MixpanelTracker.trackFunction({ functionName: FWFunction.valuation, status: "success", cellAddress: invocation.address });

    return [[valuation]];
  } catch (err) {
    return ErrorHandlerUtil.errorHandler(err, FWFunction.valuation, invocation.address, { assetIds: !!assetIds, fundIds: !!fundIds });
  }
}

/**
 * List details of all the forecast scenarios.
 * @customfunction
 * @helpUrl https://fundwave.com/guide/reporting/Captable+Functions#3de6b3d6151641a18bb9642b3cffa18a
 * @param {string[][]} [properties] Comma separated string of Field names
 * @param {string} [captableId] ID of captable whose scenarios to fetch
 * @param {number} [horizontal] If 1, horizontally displayed. If omitted, vertical display is used
 * @param {boolean} [showHeaders] Display headers
 * @returns {string[][]} A list of forecast scenarios
 * @param {CustomFunctions.Invocation} invocation Invocation object.
 * @requiresAddress
 */
export async function forecastscenarios(properties, captableId, horizontal, showHeaders, invocation) {
  try {
    const { ScenarioAPIClient } = await Utils.prepareOpenAPIClient(Utils.APIClient.instrument);

    const payload = Utils.getMicroservicePayload();
    const scenarios = await ScenarioAPIClient.getScenarios({ team: payload.team });

    if (!Boolean(scenarios?.length)) throw ErrorCode.DataNotFound;

    properties = Utils.getPropertiesInStringFormat(properties, "name");

    MixpanelTracker.trackFunction({ functionName: FWFunction.forecastScenarios, status: "success", cellAddress: invocation.address });

    return Utils.formatArray(scenarios, properties, horizontal, showHeaders, "forecastScenarios");
  } catch (err) {
    return ErrorHandlerUtil.errorHandler(err, FWFunction.forecastScenarios, invocation.address);
  }
}

/**
 * List details of all the forecast rounds.
 * @customfunction
 * @helpUrl https://fundwave.com/guide/reporting/Captable+Functions#a46a6f3b140b40138cc92f0715935331
 * @param {string[][]} [properties] Comma separated string of Field names
 * @param {number} [assetId] ID of asset whose forecast rounds to fetch
 * @param {string} [captableId] ID of captable whose forecast rounds to fetch
 * @param {string} [scenarioId] ID of scenario whose forecast rounds to fetch
 * @param {number} [horizontal] If 1, horizontally displayed. If omitted, vertical display is used
 * @param {boolean} [showHeaders] Display headers
 * @returns {string[][]} A list of forecasted rounds of the given captable
 * @param {CustomFunctions.Invocation} invocation Invocation object.
 * @requiresAddress
 */
export async function forecastrounds(properties, assetId, captableId, scenarioId, horizontal, showHeaders, invocation) {
  try {
    const teamId = localStorage.getItem("team");
    if (!teamId) return [["Please select a team from sidebar"]];

    if (!captableId && !assetId) throw ErrorCode.MissingOneOfProps;

    if (!assetId && captableId) assetId = captableId; // no use of captableId

    const { RoundAPIClient } = await Utils.prepareOpenAPIClient(Utils.APIClient.instrument);

    const payload = Utils.getMicroservicePayload([assetId.toString()], "ASSET");

    const allRoundsRaw = (await RoundAPIClient.getRounds(payload)) || [];
    let forecastRounds = allRoundsRaw
      .filter((round) => round.type === "FORECAST")
      .map((round) => {
        return {
          ...round,
          scenarioId: round.forecastDetails?.scenarioId || null,
          captableId: round.context.id,
          date: Utils.getExcelDateFromJS(round.date, true)
        };
      });

    if (scenarioId) forecastRounds = forecastRounds.filter((forecastRound) => forecastRound.scenarioId === scenarioId);

    if (!Boolean(forecastRounds?.length)) throw ErrorCode.DataNotFound;

    properties = Utils.getPropertiesInStringFormat(properties, "name");

    MixpanelTracker.trackFunction({ functionName: FWFunction.forecastRounds, status: "success", cellAddress: invocation.address });

    return Utils.formatArray(forecastRounds, properties, horizontal, showHeaders, "forecastRounds");
  } catch (err) {
    return ErrorHandlerUtil.errorHandler(err, FWFunction.forecastRounds, invocation.address);
  }
}

/**
 * @customfunction
 * @helpUrl https://fundwave.com/guide/reporting/FX+Rate+Function
 * @param {string} [baseCurrency] The base currency code
 * @param {string} [toCurrency] The target currency code
 * @param {number} [date] The date for which the exchange rate is requested.
 * @returns {string[][]} Returns exchange rate on basis of input
 * @param {CustomFunctions.Invocation} invocation Invocation object.
 * @requiresAddress
 */
export async function fxrate(baseCurrency, toCurrency, date, invocation) {
  try {
    if (!baseCurrency || !toCurrency || !date) throw ErrorCode.MissingRequiredProps;

    await Utils.prepareOpenAPIClient();
    date = Utils.getFormattedDate(date).split("T")[0];

    const { GET_HEADERS, Common } = await import("../apiclient/common.ts");
    const { OIDCClient } = await import("../apiclient/oidc-client.ts");

    const url = Utils.buildUrl("/api/exchangeRate", { baseCurrencyCode: baseCurrency, toCurrencyCode: toCurrency, time: date });

    const exchangeRate = await fetch(url, { headers: await OIDCClient.prepareHeaders(GET_HEADERS) })
      .then((resp) => Common.handleResponse(resp, FWFunction.fxRate))
      .then((resp) => resp.json());

    if (!exchangeRate) throw ErrorCode.DataNotFound;

    MixpanelTracker.trackFunction({ functionName: FWFunction.fxRate, status: "success", cellAddress: invocation.address });

    return [[exchangeRate]];
  } catch (error) {
    return ErrorHandlerUtil.errorHandler(error, FWFunction.fxRate, invocation.address, { baseCurrency: !!baseCurrency, toCurrency: !!toCurrency, date: !!date });
  }
}

/**
 * @customfunction
 * @helpUrl https://fundwave.com/guide/reporting/Metric+Functions#1c3dc448e93f80738201e4773854812c
 * @param {string[][]} [categoryGroups] Comma separated string of metric category group names
 * @param {number} [horizontal] If 1, horizontally displayed. If omitted, vertical display is used
 * @returns {string[][]} Returns metric categories
 * @param {CustomFunctions.Invocation} invocation Invocation object.
 * @requiresAddress
 */
export async function metriccategories(categoryGroups, horizontal, invocation) {
  try {
    const { MetricAPI } = await import("../apiclient/metric.js");
    if (!!categoryGroups) categoryGroups = Utils.convert2DArrayto1DArray(categoryGroups);

    const verticals = (await MetricAPI.getMetricVerticals(categoryGroups)) || [];
    if (!verticals.length) throw ErrorCode.DataNotFound;

    // verticals.push(Object.values(verticalTypeNamesMap))
    const properties = Utils.handleProperties();

    MixpanelTracker.trackFunction({ functionName: FWFunction.metricCategories, status: "success", cellAddress: invocation.address });
    const result = Utils.formatArray(verticals, properties, horizontal, undefined, "verticals");
    return result;
  } catch (error) {
    return ErrorHandlerUtil.errorHandler(error, FWFunction.metricCategories, invocation.address, { categoryGroups: !!categoryGroups });
  }
}

/**
 * @customfunction
 * @helpUrl https://fundwave.com/guide/fund-administration-software/Investor+Transaction+Functions
 * @param {string[][]} [properties] Comma separated string of Field names
 * @param {number} [fundId] Fund ID, to get investor transactions
 * @param {string[][]} [investorIds] Investor Ids, to get investor transactions
 * @param {number} [toDate] to filter transactions
 * @param {number} [fromDate] to filter transactions
 * @param {string[][]} [types] Comma separated values of transaction types to filter the transactions by.
 * @param {number} [horizontal] If 1, horizontally displayed. If omitted, vertical display is used
 * @param {boolean} [showHeaders] Display headers
 * @returns {string[][]} Returns investor transactions
 * @param {CustomFunctions.Invocation} invocation Invocation object.
 * @requiresAddress
 */
export async function investortransactions(properties, fundId, investorIds, toDate, fromDate, types, horizontal, showHeaders, invocation) {
  try {
    if (!fundId) throw ErrorCode.MissingRequiredProps;

    if (toDate) toDate = Utils.getFormattedDate(toDate).split("T")[0];
    if (fromDate) fromDate = Utils.getFormattedDate(fromDate).split("T")[0];

    const { InvestorAPI } = await import("../apiclient/investor.js");

    if (!!investorIds) investorIds = Utils.convert2DArrayto1DArray(investorIds);

    if (!!types) types = Utils.convert2DArrayto1DArray(types);

    const investorTransactions  = (await InvestorAPI.getInvestorTransactions(fundId, investorIds, fromDate, toDate, types));
    if (!Boolean(investorTransactions?.length)) throw ErrorCode.DataNotFound;

    properties = Utils.handleProperties(properties, ["transactionDate", "transactionType.type", "amount"]);

    MixpanelTracker.trackFunction({ functionName: FWFunction.investorTransactions, status: "success", cellAddress: invocation.address });
    const result = Utils.formatArray(investorTransactions, properties, horizontal, showHeaders, "investor transactions");

    return result;
  } catch (error) {
    return ErrorHandlerUtil.errorHandler(error, FWFunction.investorTransactions, invocation.address, { fundId: !!fundId, investorIds: !!investorIds });
  }
}

/**
 * Get investor transaction types
 * @customfunction
 * @helpUrl https://fundwave.com/guide/fund-administration-software/Investor+Transaction+Functions
 * @param {string[][]} [properties] Comma separated string of field names. Defaults to 'type'.
 * @param {number} [fundId] Fund ID. To fetch investor transaction types of a fund.
 * @param {number} [horizontal] If 1, horizontally displayed. If omitted, vertical display is used
 * @param {boolean} [showHeaders] Display headers
 * @returns {string[][]} A list of investor transaction types 
 * @param {CustomFunctions.Invocation} invocation Invocation object.
 * @requiresAddress
 */
export async function investortransactiontypes(properties, fundId, horizontal, showHeaders, invocation) {
  try {
    await Utils.prepareOpenAPIClient();

    if (!fundId) throw ErrorCode.MissingRequiredProps;

    const { InvestorAPI } = await import("../apiclient/investor.js");

    const investortransactionTypes = await InvestorAPI.getInvestorTransactionTypes(fundId);
    if (!Boolean(investortransactionTypes?.length)) throw ErrorCode.DataNotFound;

    properties = Utils.handleProperties(properties, "type");

    MixpanelTracker.trackFunction({ functionName: FWFunction.investorTransactionTypes, status: "success", cellAddress: invocation.address });

    return Utils.formatArray(investortransactionTypes, properties, horizontal, showHeaders);
  } catch (err) {
    return ErrorHandlerUtil.errorHandler(err, FWFunction.investorTransactionTypes, invocation.address);
  }
}

/**
 * Ask AI a question and get a response
 * @customfunction
 * @helpUrl https://fundwave.com/guide/reporting/AI+Functions
 * @param {string} question The question to ask AI
 * @param {string} [source] Source context type: "DEALFLOW", "FUND" for get answers
 * @param {string} [assetId] Asset ID to include in context
 * @param {string} [dealId] Deal ID to include in context
 * @param {number} [toDate] End date for the context
 * @param {number} [fromDate] Start date for the context
 * @param {string} [additionalInstructions] Additional instructions to enhance the AI response
 * @param {string[][]} [additionalDataWithHeaders] Additional data with headers to provide more context to AI
 * @param {boolean} [includeDocumentation] Whether to include documentation in the response
 * @returns {string} Response from AI
 * @param {CustomFunctions.Invocation} invocation Invocation object.
 * @requiresAddress
 */
export async function ai(question, source, assetId, dealId, toDate, fromDate, additionalInstructions, additionalDataWithHeaders, includeDocumentation, invocation) {
  try {
    if (!question) throw ErrorCode.MissingRequiredProps;

    if (fromDate) fromDate = Utils.getFormattedDate(fromDate).split("T")[0];
    if (toDate) toDate = Utils.getFormattedDate(toDate).split("T")[0];

    const { ChatbotAPIClient } = await Utils.prepareOpenAPIClient(Utils.APIClient.chatbot);

    const { AIAPI } = await import("../apiclient/ai.js");

    if (!fromDate && !toDate && source === "FUND") {
      const payload = await AIAPI.planPayload(question, additionalInstructions, ChatbotAPIClient);
      fromDate = payload.actionBody?.fromDate;
      toDate = payload.actionBody?.toDate;
    }

    if (additionalDataWithHeaders && Array.isArray(additionalDataWithHeaders) && additionalDataWithHeaders.length > 0) {
    
      let csvData = "";

      for (const row of additionalDataWithHeaders)
        csvData += row.map(cellValue => `"${cellValue}"`).join(",") + "\n";

      if (csvData) 
        additionalInstructions = (additionalInstructions || "") + "\n Consider the following additional data:\n" + csvData;

    }
    
    const response = await AIAPI.getAnswer(
      question, 
      source, 
      assetId, 
      dealId, 
      additionalInstructions, 
      includeDocumentation, 
      fromDate, 
      toDate,
      ChatbotAPIClient
    );

    if (!response) throw ErrorCode.DataNotFound;
    
    MixpanelTracker.trackFunction({ 
      functionName: FWFunction.ai, 
      status: "success", 
      cellAddress: invocation.address,
      props: {
        hasQuestion: !!question,
        hasSource: !!source,
        hasAssetId: !!assetId,
        hasDealId: !!dealId,
        hasAdditionalInstruction: !!additionalInstructions
      }
    });
    
    return response;
  } catch (error) {
    return ErrorHandlerUtil.errorHandler(error, FWFunction.ai, invocation.address, { 
      question: !!question,
      source: !!source,
      assetId: !!assetId,
      dealId: !!dealId,
      additionalInstructions: !!additionalInstructions
    });
  }
}

/**
 * Ask the MCP server a question and get a response
 * @customfunction
 * @helpUrl https://fundwave.com/guide/reporting/AI+Functions
 * @param {string} question The question or instruction to send to the MCP server
 * @param {string} [source] Source context type: "DEALFLOW", "FUND" for getting answers
 * @param {string} [assetId] Asset ID to include in the MCP context
 * @param {string} [dealId] Deal ID to include in the MCP context
 * @param {number} [toDate] End date for the MCP context
 * @param {number} [fromDate] Start date for the MCP context
 * @param {string} [additionalInstructions] Additional instructions to guide or refine the MCP server response
 * @param {string[][]} [additionalDataWithHeaders] Additional tabular data (including headers) to provide more context to the MCP server
 * @param {boolean} [includeDocumentation] Whether to include documentation in the MCP server context
 * @returns {string} Textual response returned by the MCP server
 * @param {CustomFunctions.Invocation} invocation Invocation object.
 * @requiresAddress
 */

export async function mcp(question, source, assetId, dealId, toDate, fromDate, additionalInstructions, additionalDataWithHeaders, includeDocumentation, invocation) {
  try {
    if (!question) throw ErrorCode.MissingRequiredProps;

    if (fromDate) fromDate = Utils.getFormattedDate(fromDate).split("T")[0];
    if (toDate) toDate = Utils.getFormattedDate(toDate).split("T")[0];

    const teamWithHTTPS = localStorage.getItem("team");
    const team = teamWithHTTPS ? teamWithHTTPS.replace(/https:\/\//g, "") : null;

    const MCPClientAPIClient = await Utils.prepareOpenAPIClient(Utils.APIClient.mcpClient);

    if (additionalDataWithHeaders && Array.isArray(additionalDataWithHeaders) && additionalDataWithHeaders.length > 0) {
    
      let csvData = "";

      for (const row of additionalDataWithHeaders)
        csvData += row.map(cellValue => `"${cellValue}"`).join(",") + "\n";

      if (csvData) 
        additionalInstructions = (additionalInstructions || "") + "\n Consider the following additional data:\n" + csvData;

    }

    const userPrompt = JSON.stringify({
      ...(source && { source }),
      ...(team && { team }),
      ...(assetId && { assetId }),
      ...(dealId && { dealId }),
      ...(additionalInstructions && { additionalInstructions }),
      ...(includeDocumentation !== null && { includeDocumentation }),
      ...(fromDate && { fromDate }),
      ...(toDate && { toDate })
    });

    const config = {
      silent: true,
    };

    const { result } = await MCPClientAPIClient.chat({ userMessage: question, userPrompt, config });

    if (!result) throw ErrorCode.DataNotFound;
    
    MixpanelTracker.trackFunction({ 
      functionName: FWFunction.mcp, 
      status: "success", 
      cellAddress: invocation.address,
      props: {
        hasQuestion: !!question,
        hasSource: !!source,
        hasAssetId: !!assetId,
        hasDealId: !!dealId,
        hasAdditionalInstruction: !!additionalInstructions
      }
    });
    
    return result;
  } catch (error) {
    return ErrorHandlerUtil.errorHandler(error, FWFunction.mcp, invocation.address, { 
      question: !!question,
      source: !!source,
      assetId: !!assetId,
      dealId: !!dealId,
      additionalInstructions: !!additionalInstructions
    });
  }
}

async function getDealProperties() {
  const { DealsAPI } = await import("../apiclient/deals.js");

  try {
    let dealProps = await DealsAPI.getCustomFields();

    let dealProperties = [{ name: "id", title: "Deal ID", type: "string" }];
    dealProps.forEach((group) => {
      group.fields.forEach((field) => {
        dealProperties.push({
          name: field.name,
          title: field.name,
          type: field.datatype?.type
        });
      });
    });

    dealProperties = dealProperties.concat([
      { name: "Deal Status", title: "Deal Status", type: "number" },
      { name: "stage", title: "Stage", type: "dropdown" },
      { name: "dealCreationDate", title: "Deal Creation Date", type: "date" },
      { name: "Status Reason", title: "Status Reason", type: "string" },
      { name: "Status Comment", title: "Status Comment", type: "string" }
    ]);

    return dealProperties;
  } catch (err) {
    console.log(err);
    return [];
  }
}

/**
 * Helper function to get entity properties including custom fields
 * @param {Object} context - Context to fetch custom fields for (e.g., { type: "ASSET" }, { type: "INVESTOR" })
 * @param {Array} baseProperties - Base properties array for the entity
 * @param {Object} [secondaryContext] - Optional secondary context for fetching custom fields
 * @returns {Array} Properties array with custom fields appended
 */
async function getEntityPropertiesWithCustomFields(context, baseProperties, secondaryContext) {
  const { CustomFieldAPIClient } = await Utils.prepareOpenAPIClient(Utils.APIClient.customField);
  const { team } = Utils.getMicroservicePayload();
  const requestBody = {
    team: { id: `https://${team.id}`, type: team.type },
    context: context, 
    ...(secondaryContext ? { secondaryContext } : {}),
  };

  try {
    const customFields = await CustomFieldAPIClient.getCustomFields(requestBody);
    return baseProperties.concat(customFields || []);
  } catch (err) {
    console.log(err);
    return baseProperties;
  }
}

async function getAssetProperties(entity) {
  return await getEntityPropertiesWithCustomFields({ type: "ASSET" }, Utils.assetFields);
}

async function getInvestorProperties(entity) {
  const baseProperties = entity === "investor" ? Utils.investorFields : Utils.investorsFields;
  return await getEntityPropertiesWithCustomFields({ type: "INVESTOR" }, baseProperties);
}

async function getVendorProperties() {
  return await getEntityPropertiesWithCustomFields({ type: "VENDOR" }, Utils.vendorFields);
}

async function getLeadProperties(fundId) {
  const leadFields = await getEntityPropertiesWithCustomFields(fundId ? { type: "FUND", id: fundId } : { type: "FUND" }, Utils.leadFields, { type: "LEAD" });
  return leadFields.map((field) => ({ ...field, name: field.key ? field.key : field.name })).sort((a, b) => a?.key?.localeCompare(b?.key));
}

async function fetchFundCurrencyFromAssetId(assetId) {
  let fundCurrency = "USD";

  const { FundAPI } = await import("../apiclient/fund.js");
  let assetFunds = await FundAPI.getAssetFunds(assetId);

  if (assetFunds?.length < 1) return fundCurrency;

  let assetFund = assetFunds[0];
  if (assetFund?.fund?.currency?.currencyCode) fundCurrency = assetFund.fund.currency.currencyCode;

  return fundCurrency;
}

function getDealsFilter(statusFilter) {
  let filter = {};

  if (!statusFilter) return { open: true };

  let statuses = statusFilter.toLowerCase().split(",");

  statuses.forEach((val) => {
    val = typeof val === "string" ? val.trim() : val;

    if (["active", "Active", "open", "ACTIVE"].includes(val)) filter.open = true;

    if (["invested", "invest", "INVESTED", "Invested"].includes(val)) filter.won = true;

    if (["drop", "dropped", "rejected", "Dropped"].includes(val)) filter.lose = true;

    if (["hold", "archive", "Hold", "on Hold"].includes(val)) filter.archive = true;
  });

  if (Object.keys(filter).length === 0) filter = { open: true };

  return filter;
}

function getMatrixFromMetricValues(metricValues, metricCodes, assetIds) {
  //create an empty metricValuesMatrix with zeroes
  const metricValuesMatrix = new Array(metricCodes.length);
  for (let index = 0; index < metricCodes.length; index++) metricValuesMatrix[index] = new Array(assetIds.length).fill("-");

  for (let metricValue of metricValues) {
    const metricCode = metricValue.metric?.code;
    const assetId = metricValue.assetId.toString();
    let rowIndex = metricCodes.indexOf(metricCode);
    let columnIndex = assetIds.indexOf(assetId);
    if (rowIndex < 0 || columnIndex < 0) continue;

    metricValuesMatrix[rowIndex][columnIndex] = metricValue;
  }

  return metricValuesMatrix;
}

async function getVariableValues(variableCodes, toDate, fromDate, assetIds, investorCodes, fundIds, assetAccountIds, displayCurrency, groupBy, ignoreTransfers, properties, horizontal, invocation, functionName) {
  try {
    if (!variableCodes) throw ErrorCode.MissingRequiredProps;
    if (!assetIds && !investorCodes && !fundIds && !assetAccountIds) throw ErrorCode.MissingOneOfProps;

    if ((investorCodes || assetAccountIds) && !fundIds) return [["Please enter a fundId"]];
    await Utils.prepareOpenAPIClient();

    let negate;

    assetIds = Boolean(assetIds?.length) ? Utils.convert2DArrayto1DArray(assetIds) : [];
    investorCodes = Boolean(investorCodes?.length) ? Utils.convert2DArrayto1DArray(investorCodes) : [];
    fundIds = Boolean(fundIds?.length) ? Utils.convert2DArrayto1DArray(fundIds) : [];
    assetAccountIds = Boolean(assetAccountIds?.length) ? Utils.convert2DArrayto1DArray(assetAccountIds) : [];

    if (groupBy) {
      groupBy = groupBy.toLowerCase();
      if (groupBy.includes("assetaccount")) groupBy = VariableGroupBy.Assetaccount;
      else if (groupBy.includes("asset")) groupBy = VariableGroupBy.Asset;
      else if (groupBy.includes("fund")) groupBy = VariableGroupBy.Fund;
      else if (groupBy.includes("investor")) groupBy = VariableGroupBy.InvestorCode;
      negate = true;
    }

    let multipleCount = 0;
    if (assetIds?.length > 1) multipleCount += 1;
    if (investorCodes?.length > 1) multipleCount += 1;
    if (fundIds?.length > 1) multipleCount += 1;
    if (assetAccountIds?.length > 1) multipleCount += 1;

    if (multipleCount > 1 && groupBy) return [["Please enter a single assetId, investorCode, assetAccountId or fundId"]];

    //groupBy should be related to any of multiple entries, otherwise error message
    if (
      multipleCount > 0 &&
      groupBy &&
      !(
        (assetIds?.length > 0 && groupBy === VariableGroupBy.Asset) ||
        (investorCodes?.length > 0 && groupBy === VariableGroupBy.InvestorCode) ||
        (fundIds?.length > 0 && groupBy === VariableGroupBy.Fund) ||
        (assetAccountIds?.length > 0 && groupBy === VariableGroupBy.Assetaccount)
      )
    )
      return [["Please select groupBy property correctly"]];

    const { VariableAPIClient } = await Utils.prepareOpenAPIClient(Utils.APIClient.fundwave);

    let context = {
      toDate: !!toDate ? Utils.getFormattedDate(toDate, false) : Utils.getFormattedDate(Utils.getExcelDateFromJS(new Date()), false)
    };

    if (!!fromDate) context.fromDate = Utils.getFormattedDate(fromDate, false);

    if (investorCodes?.length > 0) {
      context["investorCodes"] = investorCodes;
    }
    if (assetIds?.length > 0) {
      context["assetIds"] = assetIds;
    }
    if (assetAccountIds?.length > 0) {
      context["assetAccountIds"] = assetAccountIds;
    }

    if (fundIds?.length > 0) {
      context["fundIds"] = fundIds;
    }

    if (displayCurrency) {
      if (!InputValidator.isValidCurrencyCode(displayCurrency)) {
        return [[InputValidator.INVALID_CURRENCY_CODE]];
      }
      context["outputCurrencyCode"] = displayCurrency.toUpperCase();
    }

    context["codes"] = Utils.convert2DArrayto1DArray(variableCodes);
    context["negate"] = negate;
    if (groupBy) context["groupBy"] = [groupBy];

    const values = await VariableAPIClient.getVariableValues(context, ignoreTransfers);
    const variableValues = Utils.formatVariableValueResponse(values, groupBy, context);

    if (!properties) properties = "value";
    if (!properties.split(",").includes("value")) properties += ",value";

    let result = Utils.formatArray(variableValues, properties, false, false, "variableValues");

    if (horizontal) result = Utils.transposeOfMatrix(result);

    MixpanelTracker.trackFunction({ functionName: functionName, status: "success", cellAddress: invocation.address });

    return result;
  } catch (err) {
    return ErrorHandlerUtil.errorHandler(err, functionName, invocation.address);
  }
}

/**
 * @customfunction
 * @description Get the most recent 10,000 activities for the provided filters.
 * @helpUrl https://fundwave.com/guide/fund-administration-software/Activity+Functions
 * @param {string[][]} [properties] Comma separated string of Field names
 * @param {number} [toDate] The date up to which activities should be retrieved
 * @param {number} [fromDate] The date from which activities should be retrieved
 * @param {string} [entityType] Filter by an entity type (e.g., "NOTICE", "BANK_DETAILS", "CONTACT_DETAILS")
 * @param {string[][]} [fundIds] Fund Ids. If specified, activities will be shown for the specified funds
 * @param {string[][]} [investorIds] Investor Ids. If specified, activities will be shown for the specified investors
 * @param {string[][]} [assetIds] Asset Ids. If specified, activities will be shown for the specified assets
 * @param {number} [horizontal] If 1, horizontally displayed. If omitted, vertical display is used
 * @param {boolean} [showHeaders] Display headers
 * @returns {string[][]} Recent activities
 * @param {CustomFunctions.Invocation} invocation Invocation object.
 * @requiresAddress
 */
export async function activity(properties, toDate, fromDate, entityType, fundIds, investorIds, assetIds, horizontal, showHeaders, invocation) {
  try {
    if (!toDate && !fromDate && !fundIds && !investorIds && !assetIds) throw ErrorCode.MissingOneOfProps;

    if (!!fundIds) fundIds = Utils.convert2DArrayto1DArray(fundIds);
    if (!!investorIds) investorIds = Utils.convert2DArrayto1DArray(investorIds);
    if (!!assetIds) assetIds = Utils.convert2DArrayto1DArray(assetIds);

    const { ActivityAPIClient } = await Utils.prepareOpenAPIClient(Utils.APIClient.activity);
    
    let team = localStorage.getItem("team");
    team = team ? team.replaceAll("https://", "").replaceAll("http://", "") : "";
    
    let requestBody = {
      context: [],
      team: team ? { id: [team], type: "INSTANCE" } : undefined
    };

    if (fromDate) {
      requestBody.fromDate = Utils.getFormattedDate(fromDate).split("T")[0];
    }
    
    if (toDate) {
      const nextDay = new Date(Utils.getFormattedDate(toDate));
      nextDay.setDate(nextDay.getDate() + 1);
      requestBody.toDate = nextDay.toISOString().split('T')[0];
    }

    // last 10000 activities
    requestBody.pageNum = 1;
    requestBody.pageSize = 10000; 

    const contextEntries = [];
      
    if (fundIds && fundIds.length > 0) contextEntries.push({ id: fundIds.map(id => id.toString()), type: "FUND" });
    
    if (assetIds && assetIds.length > 0) contextEntries.push({ id: assetIds.map(id => id.toString()), type: "ASSET" });
    
    if (investorIds && investorIds.length > 0) contextEntries.push({ id: investorIds.map(id => id.toString()), type: "INVESTOR" });

    if (entityType && entityType.trim()) requestBody.entityType = entityType.trim().toUpperCase();
    
    
    // If no IDs provided, include default context types
    if (contextEntries.length === 0) {
      contextEntries.push({ id: [], type: "FUND" });
      contextEntries.push({ id: [], type: "ASSET" });
      contextEntries.push({ id: [], type: "INVESTOR" });
      requestBody.includeGlobal = true; // Include global activities that don't have context id and type
    }
      
    requestBody.context = contextEntries;
    
    let activities = (await ActivityAPIClient.getActivityLogs(requestBody)) || [];
    


    if (!Boolean(activities?.length)) throw ErrorCode.DataNotFound;

    // Process the date field to ensure it's a string and convert to Excel date if needed and convert context id to number
    activities = activities.map(activity => {
      const processedActivity = { ...activity };

      if (processedActivity.context && processedActivity.context.id !== undefined && !isNaN(Number(processedActivity.context.id))) {
        processedActivity.context.id = Number(processedActivity.context.id);
      }

      processedActivity.date = Utils.getExcelDateFromJS(new Date(processedActivity.date), true);
      return processedActivity;
    });

    properties = Utils.handleProperties(properties, ["type", "title", "date", "entity.type"]);
    return  Utils.formatArray(activities, properties, horizontal, showHeaders, "activity");
  } catch (err) {
    console.log("Error in activity function:", err);
    return ErrorHandlerUtil.errorHandler(err, FWFunction.activity, invocation.address, {
      fundIds: !!fundIds,
      investorIds: !!investorIds,
      assetIds: !!assetIds,
      toDate: !!toDate,
      fromDate: !!fromDate,
    });
  } finally {
    MixpanelTracker.trackFunction({ 
      functionName: FWFunction.activity, 
      status: "success", 
      cellAddress: invocation?.address,
      props: {
        hasProperties: !!properties,
        hasToDate: !!toDate,
        hasFromDate: !!fromDate,
        entityType: entityType,
        hasFundIds: !!fundIds,
        hasInvestorIds: !!investorIds,
        hasAssetIds: !!assetIds,
      } 
    });
  }
}

CustomFunctions.associate("REALM", realm);
CustomFunctions.associate("IRRA", irra);
CustomFunctions.associate("IRR", irr);
CustomFunctions.associate("ASSETACCOUNTS", assetaccounts);
CustomFunctions.associate("ASSETACCOUNT", assetaccount);
CustomFunctions.associate("ACCOUNTCODES", accountcodes);
CustomFunctions.associate("VARIABLES", variables);
CustomFunctions.associate("VARIABLEVALUE", variablevalue);
CustomFunctions.associate("INVESTORVALUE", investorvalue);
CustomFunctions.associate("ASSET", asset);
CustomFunctions.associate("ASSETUPDATETYPES", assetupdatetypes);
CustomFunctions.associate("SUBMISSIONUPDATETYPES", submissionupdatetypes);
CustomFunctions.associate("ASSETUPDATES", assetupdates);
CustomFunctions.associate("SUBMISSIONUPDATES", submissionupdates);
CustomFunctions.associate("ASSETS", assets);
CustomFunctions.associate("UNDERLYINGASSETS", underlyingAssets);
CustomFunctions.associate("METRICS", metrics);
CustomFunctions.associate("METRICVALUE", metricvalue);
CustomFunctions.associate("FUND", fund);
CustomFunctions.associate("FUNDUPDATETYPES", fundupdatetypes);
CustomFunctions.associate("FUNDUPDATES", fundupdates);
CustomFunctions.associate("FUNDS", funds);
CustomFunctions.associate("LEADS", leads);
CustomFunctions.associate("LEAD", lead);
CustomFunctions.associate("INVESTOR", investor);
CustomFunctions.associate("INVESTORS", investors);
CustomFunctions.associate("NOTICEGROUPS", noticegroups);
CustomFunctions.associate("VENDOR", vendor);
CustomFunctions.associate("VENDORS", vendors);
CustomFunctions.associate("JOURNALLINES", journallines);
CustomFunctions.associate("FUNDTRANSACTIONS", fundtransactions);
CustomFunctions.associate("FUNDTRANSACTIONTYPES", fundtransactiontypes);
CustomFunctions.associate("PROPERTIES", properties);
CustomFunctions.associate("DEALS", deals);
CustomFunctions.associate("DEALSCOUNT", dealscount);
CustomFunctions.associate("DEALNOTES", dealnotes);
CustomFunctions.associate("ROUND", round);
CustomFunctions.associate("ROUNDS", rounds);
CustomFunctions.associate("CAPTABLES", captables);
CustomFunctions.associate("SHAREHOLDERS", shareholders);
CustomFunctions.associate("FDSTAKE", fdstake);
CustomFunctions.associate("PREMONEY", premoney);
CustomFunctions.associate("POSTMONEY", postmoney);
CustomFunctions.associate("RAISED", raised);
CustomFunctions.associate("VALUATION", valuation);
CustomFunctions.associate("FORECASTSCENARIOS", forecastscenarios);
CustomFunctions.associate("FORECASTROUNDS", forecastrounds);
CustomFunctions.associate("FXRATE", fxrate);
CustomFunctions.associate("METRICCATEGORIES", metriccategories);
CustomFunctions.associate("INVESTORTRANSACTIONS", investortransactions);
CustomFunctions.associate("INVESTORTRANSACTIONTYPES", investortransactiontypes);
CustomFunctions.associate("AI", ai);
CustomFunctions.associate("MCP", mcp);
CustomFunctions.associate("ACTIVITY", activity);